import { axiosClient } from '../../../library/Service/axiosClient';

/**
 * Mentorship Phase 1 API client (Institute Admin).
 *
 * Logical backend mount: /api/v1/mentorship
 * Frontend composition follows the existing REACT_APP_COMMON_API convention
 * used by /social and /profile (no new env variable).
 *
 * Gateway rewrite of /common/v1 → /api/v1 remains DEPLOYMENT VERIFICATION REQUIRED
 * (DEC-003). This file does not invent a compensating path.
 */
const MENTORSHIP_BASE = `${process.env.REACT_APP_COMMON_API}/mentorship`;

export const MENTOR_EXPERIENCE_THRESHOLDS = [
    '10+ Years',
    '8-10 Years',
    '5-7 Years',
    'No Minimum Threshold',
] as const;

export const MENTEE_MODES = ['Opt-in', 'Auto-Enroll', 'Admin-Controlled'] as const;

export type MentorExperienceThreshold = (typeof MENTOR_EXPERIENCE_THRESHOLDS)[number];
export type MenteeMode = (typeof MENTEE_MODES)[number];

export interface EligibleProgramSelection {
    programId: number;
    years: number[];
}

export interface MentorshipCampusOption {
    label: string;
    value: number;
}

export interface MentorshipProgramOption {
    programId: number;
    name: string;
    campusId: number;
    years: number[];
    programLevel: number | null;
}

export interface MentorshipCohortListItem {
    id: number;
    instituteId: number | null;
    name: string | null;
    startDate: string | null;
    endDate: string | null;
    reshuffleEnabled: boolean;
    associatedCampusIds: number[];
    matchingLockInEffect: boolean;
}

export interface MentorshipCampusConfig {
    id?: number;
    cohortId?: number;
    campusId: number;
    mentorExperienceThreshold: string | null;
    maxMenteesPerMentor: number | null;
    menteeTarget: number | null;
    eligiblePrograms: EligibleProgramSelection[] | null;
    menteeMode: string | null;
    allowOptOutMidCohort: boolean;
    allowMentorChangeRequest: boolean;
    /** True when this campus was configured on its own tab, so it outranks the All Campuses default. */
    hasCampusOverride: boolean;
    eligibleAlumniCount: number | null;
    eligibleStudentCount: number | null;
    /** ALL_PROGRAM_CODE-shaped rows for this campus, read from the same database as the counts. */
    programs?: MentorshipProgramRow[];
    confirmedMentors: number;
    neededMentors: number | null;
    gap: number | null;
}

/** Domain-data row shape, produced either by the snapshot or by the campus config API. */
export interface MentorshipProgramRow {
    code: string | number;
    value: string;
    parentCode: string | number;
    categoryCode?: string;
    displayOrder?: number | null;
    flexCol?: string | null;
}

export interface MentorshipCohortDetail extends MentorshipCohortListItem {
    campuses: MentorshipCampusConfig[];
}

export interface MentorshipCohortIdentityPayload {
    name: string | null;
    startDate: string | null;
    endDate: string | null;
    reshuffleEnabled: boolean;
}

export interface MentorshipCreateCohortPayload extends MentorshipCohortIdentityPayload {
    campusIds?: number[];
}

export interface MentorshipCampusConfigPayload {
    mentorExperienceThreshold: string | null;
    maxMenteesPerMentor: number | null;
    menteeTarget: number | null;
    eligiblePrograms: EligibleProgramSelection[];
    menteeMode: string | null;
    allowOptOutMidCohort: boolean;
    allowMentorChangeRequest: boolean;
}

export interface MentorshipTaxonomyChild {
    id?: number;
    name: string;
}

export interface MentorshipTaxonomyTrack {
    id?: number;
    name: string;
    children: MentorshipTaxonomyChild[];
    potentialMentorCount?: number | null;
}

export type MentorshipMappingRunStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface MentorshipMappingState {
    runId: number | null;
    status: MentorshipMappingRunStatus | null;
    lastCalculatedAt: string | null;
    /** Recalculate is allowed once per 7 days per cohort; decided by the server. */
    recalculateLocked: boolean;
    recalculateAvailableAt: string | null;
}

export interface MentorshipDomainTaxonomy {
    tracks: MentorshipTaxonomyTrack[];
    mapping: MentorshipMappingState;
}

export interface MentorshipDomainTaxonomyPayload {
    tracks: MentorshipTaxonomyTrack[];
}

export interface MentorshipRecalculateResult {
    runId: number | null;
    status: MentorshipMappingRunStatus;
}

export const MENTOR_POOL_SOURCES = ['System Identified', 'Watchlist', 'File Upload', 'Manually added'] as const;
export const MENTOR_POOL_CONFIRMATION_STATUSES = ['Invited', 'Confirmed', 'Onboarded', 'Declined'] as const;
export const MENTOR_POOL_EXPERIENCE_BUCKETS = ['<5', '5-8', '8-10', '10+'] as const;
export const MENTOR_POOL_ENGAGEMENT_LEVELS = ['HIGH', 'MODERATE', 'LOW'] as const;
export const MENTOR_POOL_PAGE_SIZE_DEFAULT = 25;
export const MENTOR_POOL_ALREADY_IN_POOL = 'This user is already a part of the mentor pool';
export const MENTOR_POOL_REMINDER_NOT_CONFIGURED = 'Mentor reminder is not yet configured';
export const MENTOR_POOL_UNKNOWN_USER_NOT_CONFIGURED =
    'Creating a new semi-verified user is not yet configured.';
/** User-facing copy when a SendGrid / EmailUtils failure dump leaks into Mentorship UI (e.g. matching initiator mail). */
export const MENTORSHIP_EMAIL_SEND_FAILED_USER_MESSAGE =
    'Confirmation email could not be sent. Matching results are still available.';
export const MENTOR_POOL_MESSAGE_RECIPIENT_LIMIT = 20;
export const MENTOR_POOL_MESSAGE_LIMIT_MESSAGE = 'You can select upto 20 records to send message.';

export const MENTEE_POOL_CONFIRMATION_STATUSES = ['Yet to register', 'Registered', 'Opted-out'] as const;
export const MENTEE_POOL_PAGE_SIZE_DEFAULT = 25;
export const MENTEE_POOL_PAGE_SIZES = [25, 50, 100] as const;
export const SESSION_LOG_PAGE_SIZE_DEFAULT = 25;
export const SESSION_LOG_PAGE_SIZES = [25, 50, 100] as const;
export const SESSION_LOG_LOGGED_BY_ROLES = ['SUPER_ADMIN', 'INSTITUTE_ADMIN', 'MENTOR'] as const;
export const MENTEE_POOL_ALREADY_IN_POOL = 'This user is already a part of the mentee pool';
export const MENTEE_POOL_ELIGIBILITY_CONFLICT = 'Please check the cohort eligibility criteria for mentees.';
/** API / DEC-P4-019 machine value for unknown-student create (do not show raw in UI). */
export const MENTEE_POOL_UNKNOWN_STUDENT_CODE = 'not_configured';
/** User-facing Manual Add copy when search finds no eligible existing student. Create remains DEC-P4-019. */
export const MENTEE_POOL_UNKNOWN_STUDENT =
    'No eligible students matched your search. Creating a new student is not yet configured.';
export const MENTEE_POOL_REGISTRATION_DELIVERY = 'not_configured';
export const MENTEE_POOL_UNAVAILABLE = 'Unavailable';
export const MENTEE_POOL_MODULE_SETTINGS_PATH = '/management/mentorship/module-settings';
export const MENTEE_POOL_MESSAGE_RECIPIENT_LIMIT = 20;
export const MENTEE_POOL_MESSAGE_LIMIT_MESSAGE = 'You can select upto 20 records to send message.';
export const MENTEE_POOL_BULK_CAP = 20;
export const MENTEE_POOL_DELIVERY_NOT_CONFIGURED_MESSAGE = 'Delivery is not configured yet';
export const MENTEE_POOL_DELIVERY_SENT_MESSAGE = 'Email sent.';
export const MENTEE_POOL_DELIVERY_FAILED_MESSAGE = 'Email delivery failed.';

export type MenteePoolConfirmationStatus = (typeof MENTEE_POOL_CONFIRMATION_STATUSES)[number];
export type MenteePoolBulkAction = 'registration-link' | 'reminder' | 'remove';

export const MENTEE_POOL_BULK_OVER_LIMIT: Record<MenteePoolBulkAction, string> = {
    'registration-link': 'You can select upto 20 records to send registration link.',
    reminder: 'You can select upto 20 records to send reminder.',
    remove: 'You can select upto 20 records to remove.',
};

export type MentorPoolSource = (typeof MENTOR_POOL_SOURCES)[number];
export type MentorPoolConfirmationStatus = (typeof MENTOR_POOL_CONFIRMATION_STATUSES)[number];
export type MentorPoolExperienceBucket = (typeof MENTOR_POOL_EXPERIENCE_BUCKETS)[number];

export interface MentorshipMentorPoolTrack {
    id: number;
    name: string | null;
}

export interface MentorshipMentorPoolAlumni {
    name: string | null;
    profilePicture: string | null;
    uuid: string | null;
    programName: string | null;
    batchYear: number | string | null;
    company: string | null;
    designation: string | null;
    totalWorkex: number | null;
    expertise?: string | null;
    ap: string | number | null;
    mq: string | number | null;
    vl: string | number | null;
}

export interface MentorshipMentorPoolMember {
    id: number;
    alumniId: string;
    campusId: number | null;
    source: string | null;
    confirmationStatus: string | null;
    confirmationComment: string | null;
    invitedAt: string | null;
    alumni: MentorshipMentorPoolAlumni;
    tracks: MentorshipMentorPoolTrack[];
    assignedMentees: number | null;
    maxMenteesPerMentor: number | null;
}

export interface MentorshipPagination {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
}

export interface MentorshipMentorPoolListQuery {
    search?: string;
    company?: string;
    campusId?: number | null;
    confirmationStatus?: string[];
    includePreInvite?: boolean;
    trackIds?: number[];
    experience?: string[];
    source?: string[];
    page?: number;
    pageSize?: number;
}

export interface MentorshipMentorPoolListResult {
    members: MentorshipMentorPoolMember[];
    pagination: MentorshipPagination;
}

export interface MentorshipMentorPoolCandidate {
    alumniId: string;
    campusId: number | null;
    campusAmbiguous: boolean;
    campusName?: string | null;
    source?: string | null;
    watchlistCodes?: string[];
    alumni: MentorshipMentorPoolAlumni;
    tracks: MentorshipMentorPoolTrack[];
}

export interface MentorshipInvitationDeliveryCounts {
    sent: number;
    disabledBySettings: number;
    sendFailed: number;
}

export interface MentorshipMentorInviteResult {
    sent: boolean;
    reason?: string | null;
    invitedAt: string | null;
    confirmationStatus: string | null;
}

export type MentorshipInviteTone = 'success' | 'warning' | 'danger';

export interface MentorshipInviteFeedback {
    tone: MentorshipInviteTone;
    text: string;
}

export interface MentorshipImportResult {
    imported: Array<{
        alumniId?: string;
        memberId?: number;
        row?: number;
        identifier?: string;
        /** Mentee upload only: track names in the file that are not in this cohort's taxonomy. */
        skippedTracks?: string[];
    }>;
    failed: Array<{ alumniId?: string; row?: number; identifier?: string; reason?: string }>;
    invitationDelivery?: MentorshipInvitationDeliveryCounts | null;
    skillsProfileWrite?: string | null;
}

export interface MentorshipManualSearchCandidate {
    alumniId: string;
    uuid: string | null;
    name: string | null;
    company: string | null;
    designation: string | null;
    /** Profile skills resolved from skill.skill_code -> domain_data, else skill.other_skill. */
    skills: string[];
    alreadyInPool: boolean;
    campuses: Array<{ campusId: number; programName: string | null }>;
}

export interface MentorshipManualAddPayload {
    alumniId: string;
    campusId: number;
    trackIds: number[];
    currentCompany: string;
    currentDesignation: string;
    skills: string[];
}

export interface MentorshipWatchlistListQuery {
    search?: string;
    expertise?: string;
    company?: string;
    designation?: string;
    quadrant?: string[];
    experience?: string[];
    ap?: string[];
    mq?: string[];
    vl?: string[];
    campusId?: number | null;
    page?: number;
    pageSize?: number;
}

export interface MentorshipMenteePoolStudent {
    name: string | null;
    uuid: string | null;
    email: string | null;
    profilePicture: string | null;
    programName: string | null;
    batchYear: number | string | null;
}

export interface MentorshipMenteePoolMember {
    id: number;
    studentId: string | null;
    campusId: number | null;
    confirmationStatus: string | null;
    confirmationComment: string | null;
    student: MentorshipMenteePoolStudent;
    tracks: MentorshipMentorPoolTrack[];
    assignedMentor: string | null;
    assignmentDisplayStatus: string | null;
    preferredMentorshipAreas: string[] | string | null;
    mentorshipGoals: string | null;
}

export interface MentorshipMenteePoolListQuery {
    search?: string;
    campusId?: number | null;
    confirmationStatus?: string[];
    trackIds?: number[];
    programIds?: number[];
    /** Calendar pass_out_year values (not Year-N). */
    years?: number[];
    page?: number;
    pageSize?: number;
}

export interface MentorshipMenteePoolListResult {
    members: MentorshipMenteePoolMember[];
    pagination: MentorshipPagination;
}

export interface MentorshipMenteePoolSearchCandidate {
    studentId: string;
    uuid: string | null;
    name: string | null;
    email: string | null;
    alreadyInPool: boolean;
    campuses: Array<{ campusId: number; programName: string | null }>;
}

export interface MentorshipMenteePoolAddPayload {
    studentId: string;
    campusId: number;
    trackIds: number[];
}

export interface MentorshipMenteePoolBulkResult {
    action: MenteePoolBulkAction;
    processed: Array<{
        memberId?: number;
        confirmationStatus?: string | null;
        registrationDelivery?: string | null;
    }>;
    failed: Array<{ memberId?: number; reason?: string }>;
    registrationDelivery?: string | null;
}

export const emptyMappingState = (): MentorshipMappingState => ({
    runId: null,
    status: null,
    lastCalculatedAt: null,
    recalculateLocked: false,
    recalculateAvailableAt: null,
});

export const emptyCampusConfigPayload = (): MentorshipCampusConfigPayload => ({
    mentorExperienceThreshold: null,
    maxMenteesPerMentor: null,
    menteeTarget: null,
    eligiblePrograms: [],
    menteeMode: null,
    allowOptOutMidCohort: false,
    allowMentorChangeRequest: false,
});

/** Exact REQ-2371-07 / SPEC.md copy. Do not rewrite. */
export const MATCHING_BACKGROUND_BANNER =
    'The matching is running in background. Please come back once you receive the confirmation email that matching algorithm has run completely';

/** REQ-2371-05 locked-state wording from SPEC.md. */
export const MATCHING_GATE_LOCKED_COPY =
    'running the matching algorithm will be unlocked once 70% profiles are confirmed';

export const MATCHING_RUN_POLL_MS = 5000;
export const MATCHING_IA_RUN_LIMIT = 2;
export const MATCHING_IA_LIMIT_MESSAGE = 'The matching algorithm can only be re-run once for this cohort.';
export const MATCHING_ASSIGNMENT_STATUS_FILTERS = [
    'Pending Decision',
    'Confirmed Pairing',
    'Published Pairing',
] as const;

export type MatchingAssignmentStatusFilter = (typeof MATCHING_ASSIGNMENT_STATUS_FILTERS)[number];
export type MentorshipMatchingRunStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface MentorshipMatchingGate {
    requiredSum: number | null;
    threshold: number | null;
    confirmedOrOnboardedMentors: number;
    registeredMentees: number;
    unlocked: boolean;
    iaRunCount: number;
}

export interface MentorshipMatchingStats {
    currentRunId: number | null;
    confirmedMentors: number;
    registeredMentees: number;
    totalPairings: number;
    pendingDecision: number;
    confirmedPairings: number;
    publishedPairings: number;
    distinctMenteesPaired: number;
}

/** P9-BATCH-008/009: derived overlay. Never a stored Complete status. */
export interface MentorshipCohortProgressPair {
    pairId: number;
    menteeName: string | null;
    mentorName: string | null;
    interactionCount: number;
    sessionCount: number;
    isComplete: boolean;
    isInactive: boolean;
    lastActivityAt: string | null;
}

export interface MentorshipCohortProgress {
    windowStart: string | null;
    windowEnd: string | null;
    hasDefinition: boolean;
    interactionsEnabled: boolean;
    interactionsMinimum: number;
    oneToOneSessionsEnabled: boolean;
    oneToOneSessionsMinimum: number;
    currentRunId: number | null;
    publishedPairCount: number;
    completePairCount: number;
    inWindowSessionTotal: number;
    inWindowInteractionTotal: number;
    inactivePairCount: number;
    sessionProgressPercent: number | null;
    interactionProgressPercent: number | null;
    pairs: MentorshipCohortProgressPair[];
}

export interface MentorshipMatchingRun {
    id: number;
    cohortId: number | null;
    status: MentorshipMatchingRunStatus | null;
    initiatorRole: string | null;
    startedAt: string | null;
    finishedAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface MentorshipMatchingPairTrack {
    id: number | null;
    name: string | null;
}

export interface MentorshipMatchingPairMentee {
    id: number | null;
    campusId: number | null;
    name: string | null;
    email: string | null;
    uuid: string | null;
    profilePicture: string | null;
    programId: number | null;
    programName: string | null;
    batchYear: number | string | null;
    eligibleYears: number[];
    tracks: MentorshipMatchingPairTrack[];
}

export interface MentorshipMatchingPairMentor {
    id: number | null;
    campusId: number | null;
    name: string | null;
    email: string | null;
    uuid: string | null;
    profilePicture: string | null;
    programName: string | null;
    batchYear: number | string | null;
    company: string | null;
    designation: string | null;
    confirmationStatus: string | null;
    tracks: MentorshipMatchingPairTrack[];
}

export interface MentorshipMatchingOtherMenteeMatch {
    menteeId: number | null;
    name: string | null;
    email: string | null;
    programName: string | null;
    batchYear: number | string | null;
    rank: number | null;
    isSelected: boolean;
    assignmentStatus: string | null;
}

export interface MentorshipMatchingPair {
    id: number;
    runId: number | null;
    rank: number | null;
    isSelected: boolean;
    assignmentStatus: string | null;
    reasoning: string | null;
    otherMenteeMatches: number;
    otherMenteeMatchDetails: MentorshipMatchingOtherMenteeMatch[];
    remainingCapacity: number | null;
    mentee: MentorshipMatchingPairMentee;
    mentor: MentorshipMatchingPairMentor;
}

export type MentorshipAdminBookingMode =
    | 'ZOOM'
    | 'GOOGLE_MEET'
    | 'TEAMS'
    | 'GOOGLE_CHAT'
    | 'PHYSICAL_MEET'
    | 'OTHERS';

export interface MentorshipAdminBookingSlot {
    date: string;
    startMinute: number;
    durationMinutes: number;
    state: 'available' | 'booked' | 'past';
}

export interface MentorshipAdminBookingSlotsPayload {
    month: string;
    pairId: number | null;
    mentorAlumniId: string | null;
    mentorName: string | null;
    menteeName: string | null;
    slotDurationMinutes: number | null;
    timezoneLabel: string | null;
    slots: MentorshipAdminBookingSlot[];
}

export interface MentorshipAdminCreateBookingPayload {
    date: string;
    startMinute: number;
    topic: string;
    agenda?: string | null;
    mode: MentorshipAdminBookingMode;
    sessionLink?: string | null;
}

export interface MentorshipAdminBookingRow {
    id: number;
    pairId: number | null;
    bookedDate: string | null;
    startMinute: number | null;
    durationMinutes: number | null;
    topic: string;
    mode: string | null;
    sessionLink: string | null;
    bookedByRole: string | null;
    emailResult: unknown;
}

export interface MentorshipMatchingPairListQuery {
    search?: string;
    campusId?: number | null;
    programIds?: number[];
    years?: number[];
    trackIds?: number[];
    assignmentStatus?: string[];
}

export interface MentorshipMatchingPairListResult {
    runId: number | null;
    pairs: MentorshipMatchingPair[];
}

export interface MentorshipMatchingSelectResult {
    id: number | null;
    runId: number | null;
    menteePoolMemberId: number | null;
    mentorPoolMemberId: number | null;
    rank: number | null;
    isSelected: boolean;
    assignmentStatus: string | null;
}

export interface MentorshipMatchingConfirmAllResult {
    runId: number | null;
    confirmed: number;
    skipped: number;
}

export interface MentorshipMatchingPublishResult {
    runId: number | null;
    published: number;
    emails: string | null;
}

export interface MentorshipNotificationPreferences {
    mentorInvitationEmail: boolean;
    menteeOptInConfirmation: boolean;
    matchAnnouncement: boolean;
}

export interface MentorshipDodPreferences {
    interactionsEnabled: boolean;
    interactionsMinimum: number;
    oneToOneSessionsEnabled: boolean;
    oneToOneSessionsMinimum: number;
    showDodProgressToMentors: boolean;
    showDodProgressToMentees: boolean;
    alertAdminWhenPairFallingBehind: boolean;
}

export type MentorshipWhoCanLogOffline = 'ANY_PARTICIPANT' | 'MENTOR_AND_ADMIN' | 'ADMIN_ONLY';

export interface MentorshipOfflineLoggingPreferences {
    whoCanLogOffline: MentorshipWhoCanLogOffline;
    modeZoomTeamsMeet: boolean;
    modeTelephonic: boolean;
    modeFacetimeWhatsapp: boolean;
    modeLinkedin: boolean;
    modeInPerson: boolean;
    modeOthers: boolean;
}

export type MentorshipInteractionMode =
    | 'ZOOM_TEAMS_MEET'
    | 'TELEPHONIC'
    | 'FACETIME_WHATSAPP'
    | 'LINKEDIN'
    | 'IN_PERSON'
    | 'OTHERS';

export interface MentorshipPairSession {
    id: number;
    cohortId: number | null;
    pairId: number | null;
    source: string | null;
    occurredAt: string | null;
    durationMinutes: number | null;
    sessionLink: string | null;
    title: string | null;
    loggedByRole: string | null;
    createdBy: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface MentorshipPairInteraction {
    id: number;
    cohortId: number | null;
    pairId: number | null;
    mode: string | null;
    occurredAt: string | null;
    notes: string | null;
    loggedByRole: string | null;
    createdBy: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface MentorshipCreatePairSessionPayload {
    occurredAt: string;
    sessionLink?: string | null;
    title?: string | null;
    durationMinutes?: number | null;
}

export interface MentorshipCreatePairInteractionPayload {
    occurredAt: string;
    mode: MentorshipInteractionMode;
    notes?: string | null;
}

export type MentorshipSessionLogLoggedByRole = (typeof SESSION_LOG_LOGGED_BY_ROLES)[number];

export interface MentorshipCohortSessionLogRow {
    id: number;
    occurredAt: string | null;
    pairId: number | null;
    menteeName: string | null;
    mentorName: string | null;
    campusName: string | null;
    type: string | null;
    durationMinutes: number | null;
    title: string | null;
    sessionLink: string | null;
    loggedByRole: string | null;
    source: string | null;
    /** DEC-P18-010 — stars when rated, else null (UI shows —). */
    ratingStars: number | null;
}

export interface MentorshipCohortSessionLogQuery {
    search?: string;
    campusId?: number | null;
    loggedByRole?: string | null;
    /** Cheap has-rating filter: '' | 'rated' | 'unrated'. */
    rating?: '' | 'rated' | 'unrated';
    page?: number;
    pageSize?: number;
}

export interface MentorshipCohortSessionLogResult {
    sessions: MentorshipCohortSessionLogRow[];
    pagination: MentorshipPagination;
    sessionCount: number;
    pairCount: number;
}

class MentorshipService {
    static requestInstituteId: number | null = null;

    static setRequestInstituteId(instituteId: number | null) {
        MentorshipService.requestInstituteId = MentorshipService.toNumberOrNull(instituteId);
    }

    static withInstituteQuery(
        instituteId?: number | null,
        extraParams?: Record<string, string | number | undefined | null>
    ) {
        const resolved =
            instituteId === undefined
                ? MentorshipService.requestInstituteId
                : MentorshipService.toNumberOrNull(instituteId);
        const params: Record<string, string | number> = {};
        if (extraParams) {
            Object.entries(extraParams).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;
                params[key] = value;
            });
        }
        if (resolved != null) {
            params.instituteId = resolved;
        }
        if (Object.keys(params).length === 0) return {};
        return { params };
    }

    static readonly MENTORSHIP_SUBSCRIPTION_DISABLED_MESSAGE =
        'Mentorship Module is not a part of your subscription plan currently. Please contact your success manager for more details';

    static isMentorshipFeatureGateError(error: any): boolean {
        const status = error?.status ?? error?.response?.status;
        const message = String(error?.message ?? error?.response?.data?.message ?? error?.data?.message ?? '')
            .trim()
            .toLowerCase();
        return status === 403 && (message === 'forbidden' || message === '');
    }

    static softenEmailFailureMessage(message: string): string {
        const text = String(message ?? '');
        if (!text) return text;
        const looksLikeEmailDump =
            /email sending failed/i.test(text) ||
            (/request failed with status code 401/i.test(text) && /"attempt"\s*:/i.test(text));
        return looksLikeEmailDump ? MENTORSHIP_EMAIL_SEND_FAILED_USER_MESSAGE : text;
    }

    static getErrorMessage(error: any, options?: { instituteAdmin?: boolean }): string {
        if (options?.instituteAdmin && MentorshipService.isMentorshipFeatureGateError(error)) {
            return MentorshipService.MENTORSHIP_SUBSCRIPTION_DISABLED_MESSAGE;
        }
        const message =
            error?.message || error?.data?.message || 'An error occurred while processing your request.';
        return MentorshipService.softenEmailFailureMessage(String(message));
    }

    static unwrap<T>(response: any): T {
        return (response?.data?.data ?? response?.data) as T;
    }

    static toDateInputValue(value: any): string {
        if (value === null || value === undefined || value === '') return '';
        const text = String(value);
        if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
        return '';
    }

    static toNumberOrNull(value: any): number | null {
        if (value === null || value === undefined || value === '') return null;
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }

    static asBoolean(value: any): boolean {
        return value === true || value === 1 || value === '1';
    }

    static mapInvitationDelivery(value: any): MentorshipInvitationDeliveryCounts | null {
        if (value == null) {
            return null;
        }
        if (typeof value !== 'object') {
            return null;
        }
        return {
            sent: MentorshipService.toNumberOrNull(value.sent) ?? 0,
            disabledBySettings: MentorshipService.toNumberOrNull(value.disabledBySettings) ?? 0,
            sendFailed: MentorshipService.toNumberOrNull(value.sendFailed) ?? 0,
        };
    }

    static formatMentorInviteOutcome(result: MentorshipMentorInviteResult): MentorshipInviteFeedback {
        if (result.sent === true) {
            return { tone: 'success', text: 'Invitation email sent.' };
        }
        if (result.reason === 'disabled_by_settings') {
            return {
                tone: 'warning',
                text: 'Invitation was not sent because mentor invitation email is turned off in Notifications.',
            };
        }
        return { tone: 'danger', text: 'Invitation email could not be sent.' };
    }

    static formatInvitationDeliveryFeedback(
        delivery: MentorshipInvitationDeliveryCounts | null | undefined
    ): MentorshipInviteFeedback | null {
        if (!delivery) {
            return null;
        }
        const sent = delivery.sent || 0;
        const skipped = delivery.disabledBySettings || 0;
        const failed = delivery.sendFailed || 0;
        const text = `Invitations: ${sent} sent, ${skipped} skipped (Notifications off), ${failed} failed.`;
        if (failed > 0) {
            return { tone: 'danger', text };
        }
        if (skipped > 0) {
            return { tone: 'warning', text };
        }
        if (sent > 0) {
            return { tone: 'success', text };
        }
        return { tone: 'warning', text };
    }

    static inviteFeedbackAlertClass(tone: MentorshipInviteTone): string {
        if (tone === 'success') {
            return 'alert alert-success';
        }
        if (tone === 'warning') {
            return 'alert alert-warning';
        }
        return 'alert alert-danger';
    }

    /**
     * Campus Configuration Needed/Gap preview (R-QA-01 ISSUE-1).
     * ceil(target / max) — same rounding as the matching gate (DEC-P5-021).
     * Unavailable (null) only when target/max missing or max === 0.
     */
    static neededMentorsPreview(menteeTarget: any, maxMenteesPerMentor: any): number | null {
        const target = MentorshipService.toNumberOrNull(menteeTarget);
        const max = MentorshipService.toNumberOrNull(maxMenteesPerMentor);
        if (target === null || max === null || max === 0) return null;
        return Math.ceil(target / max);
    }

    static gapPreview(neededMentors: number | null, confirmedMentors: number = 0): number | null {
        if (neededMentors === null) return null;
        return neededMentors - confirmedMentors;
    }

    static normalizeEligiblePrograms(value: any): EligibleProgramSelection[] {
        if (!Array.isArray(value)) return [];
        return value
            .map((item: any) => {
                const programId = MentorshipService.toNumberOrNull(item?.programId ?? item?.program_id);
                if (programId === null) return null;
                const years = Array.isArray(item?.years)
                    ? item.years
                          .map((year: any) => MentorshipService.toNumberOrNull(year))
                          .filter((year: number | null): year is number => year !== null)
                    : [];
                return { programId, years };
            })
            .filter((item): item is EligibleProgramSelection => item !== null);
    }

    static mapCampusConfig(row: any): MentorshipCampusConfig {
        const maxMenteesPerMentor = MentorshipService.toNumberOrNull(
            row?.maxMenteesPerMentor ?? row?.max_mentees_per_mentor
        );
        const menteeTarget = MentorshipService.toNumberOrNull(row?.menteeTarget ?? row?.mentee_target);
        const neededRaw = row?.neededMentors ?? row?.needed_mentors;
        const neededMentors =
            neededRaw === null || neededRaw === undefined
                ? MentorshipService.neededMentorsPreview(menteeTarget, maxMenteesPerMentor)
                : MentorshipService.toNumberOrNull(neededRaw);
        const confirmedMentors =
            MentorshipService.toNumberOrNull(row?.confirmedMentors ?? row?.confirmed_mentors) ?? 0;
        const gapRaw = row?.gap;
        return {
            id: MentorshipService.toNumberOrNull(row?.id) ?? undefined,
            cohortId: MentorshipService.toNumberOrNull(row?.cohortId ?? row?.cohort_id) ?? undefined,
            campusId: MentorshipService.toNumberOrNull(row?.campusId ?? row?.campus_id) as number,
            mentorExperienceThreshold: row?.mentorExperienceThreshold ?? row?.mentor_experience_threshold ?? null,
            maxMenteesPerMentor,
            menteeTarget,
            eligiblePrograms: MentorshipService.normalizeEligiblePrograms(
                row?.eligiblePrograms ?? row?.eligible_programs
            ),
            menteeMode: row?.menteeMode ?? row?.mentee_mode ?? null,
            allowOptOutMidCohort: MentorshipService.asBoolean(
                row?.allowOptOutMidCohort ?? row?.allow_opt_out_mid_cohort
            ),
            allowMentorChangeRequest: MentorshipService.asBoolean(
                row?.allowMentorChangeRequest ?? row?.allow_mentor_change_request
            ),
            hasCampusOverride: MentorshipService.asBoolean(
                row?.hasCampusOverride ?? row?.has_campus_override
            ),
            eligibleAlumniCount: MentorshipService.toNumberOrNull(
                row?.eligibleAlumniCount ?? row?.eligible_alumni_count
            ),
            eligibleStudentCount: MentorshipService.toNumberOrNull(
                row?.eligibleStudentCount ?? row?.eligible_student_count
            ),
            // Carried through so the program picker can use the API's rows instead of the
            // ALL_PROGRAM_CODE domain snapshot. Dropping it here silently empties the picker.
            programs: Array.isArray(row?.programs) ? row.programs : [],
            confirmedMentors,
            neededMentors,
            gap:
                gapRaw === null || gapRaw === undefined
                    ? MentorshipService.gapPreview(neededMentors, confirmedMentors)
                    : MentorshipService.toNumberOrNull(gapRaw),
        };
    }

    static mapCohortIdentity(row: any): MentorshipCohortListItem {
        const associated = Array.isArray(row?.associatedCampusIds)
            ? row.associatedCampusIds
                  .map((id: any) => MentorshipService.toNumberOrNull(id))
                  .filter((id: number | null): id is number => id !== null)
            : [];
        return {
            id: MentorshipService.toNumberOrNull(row?.id) as number,
            instituteId: MentorshipService.toNumberOrNull(row?.instituteId),
            name: row?.name ?? null,
            startDate: MentorshipService.toDateInputValue(row?.startDate) || null,
            endDate: MentorshipService.toDateInputValue(row?.endDate) || null,
            reshuffleEnabled: MentorshipService.asBoolean(row?.reshuffleEnabled),
            associatedCampusIds: associated,
            matchingLockInEffect: MentorshipService.asBoolean(row?.matchingLockInEffect),
        };
    }

    static mapCohortDetail(row: any): MentorshipCohortDetail {
        const identity = MentorshipService.mapCohortIdentity(row);
        const campuses = Array.isArray(row?.campuses) ? row.campuses.map((item: any) => MentorshipService.mapCampusConfig(item)) : [];
        return { ...identity, campuses };
    }

    static payloadFromCampusConfig(config: MentorshipCampusConfigPayload): MentorshipCampusConfigPayload {
        return {
            mentorExperienceThreshold: config.mentorExperienceThreshold || null,
            maxMenteesPerMentor: MentorshipService.toNumberOrNull(config.maxMenteesPerMentor),
            menteeTarget: MentorshipService.toNumberOrNull(config.menteeTarget),
            eligiblePrograms: MentorshipService.normalizeEligiblePrograms(config.eligiblePrograms),
            menteeMode: config.menteeMode || null,
            allowOptOutMidCohort: MentorshipService.asBoolean(config.allowOptOutMidCohort),
            allowMentorChangeRequest: MentorshipService.asBoolean(config.allowMentorChangeRequest),
        };
    }

    static parseFlexCol(flexCol: any): Record<string, any> {
        if (!flexCol) return {};
        if (typeof flexCol === 'object') return flexCol;
        try {
            return JSON.parse(flexCol) || {};
        } catch {
            return {};
        }
    }

    /**
     * Year checkboxes from stored program duration only when that duration is an
     * exact whole number of years. Do not round WEEKS/DAYS into Year-N.
     */
    static yearsFromProgramDuration(flexCol: any): number[] {
        const flex = MentorshipService.parseFlexCol(flexCol);
        const unit = flex?.prog_duration;
        const desc = Number(flex?.prog_duration_desc);
        let yearCount: number | null = null;

        if (typeof unit === 'number' && Number.isInteger(unit) && unit > 0) {
            yearCount = unit;
        } else if (unit === 'YEARS' && Number.isInteger(desc) && desc > 0) {
            yearCount = desc;
        } else if (unit === 'MONTHS' && Number.isFinite(desc) && desc > 0 && desc % 12 === 0) {
            yearCount = desc / 12;
        }

        if (yearCount === null || !Number.isInteger(yearCount) || yearCount < 1) return [];
        return Array.from({ length: yearCount }, (_, index) => index + 1);
    }

    /** ALL_PROGRAM_CODE flex_col.program_level from domainDataSnapshot. */
    static programLevelFromFlex(flexCol: any): number | null {
        const flex = MentorshipService.parseFlexCol(flexCol);
        const level = MentorshipService.toNumberOrNull(flex?.program_level);
        if (level === null || !Number.isInteger(level)) return null;
        return level;
    }

    /** K12 = numeric program_level 1–12 (not UG/PG/string levels). */
    static isK12ProgramLevel(level: number | null | undefined): boolean {
        return level != null && Number.isInteger(level) && level >= 1 && level <= 12;
    }

    static k12AutoYears(program: MentorshipProgramOption): number[] {
        if (program.years.length > 0) return [...program.years];
        if (program.programLevel != null) return [program.programLevel];
        return [];
    }

    static todayCalendarDate(): string {
        const now = new Date();
        const pad2 = (value: number) => String(value).padStart(2, '0');
        return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
    }

    /** Create-only (R-ADMIN-02 ISSUE-1): trimmed name + both calendar dates. Edit unchanged. */
    static isCreateCohortIdentityComplete(identity: {
        name?: string | null;
        startDate?: string | null;
        endDate?: string | null;
    }): boolean {
        const name = identity?.name == null ? '' : String(identity.name).trim();
        const startDate = identity?.startDate == null ? '' : String(identity.startDate).trim();
        const endDate = identity?.endDate == null ? '' : String(identity.endDate).trim();
        return Boolean(name && startDate && endDate);
    }

    static getAdminCampuses(domain: Map<string, any[]> | undefined, instituteCodes: any[]): MentorshipCampusOption[] {
        const codes = Array.isArray(instituteCodes) ? instituteCodes : [];
        const institutes = domain?.get('INSTITUTE_DOMAIN') ?? [];
        const seen = new Set<number>();
        const campuses: MentorshipCampusOption[] = [];

        codes.forEach((code: any) => {
            const campus = institutes.find((item: any) => item.code == code);
            if (!campus) return;
            const flex = MentorshipService.parseFlexCol(campus.flexCol);
            if (flex.is_published != 1) return;
            const value = MentorshipService.toNumberOrNull(campus.code);
            if (value === null || seen.has(value)) return;
            seen.add(value);
            campuses.push({ label: campus.value, value });
        });

        return campuses;
    }

    static getGroupCampusesFromDomain(
        domain: Map<string, any[]> | undefined,
        selectedInstituteId: any
    ): MentorshipCampusOption[] {
        const selectedId = MentorshipService.toNumberOrNull(selectedInstituteId);
        if (selectedId === null) return [];

        const institutes = domain?.get('INSTITUTE_DOMAIN') ?? [];
        const selected = institutes.find((item: any) => MentorshipService.toNumberOrNull(item.code) === selectedId);
        const selectedFlex = MentorshipService.parseFlexCol(selected?.flexCol);
        const groupId = selectedFlex.institute_group_id;
        const hasGroup = groupId != null && groupId !== '';

        const codes = institutes
            .filter((item: any) => {
                const id = MentorshipService.toNumberOrNull(item.code);
                if (id === null) return false;
                if (id === selectedId) return true;
                if (!hasGroup) return false;
                const flex = MentorshipService.parseFlexCol(item.flexCol);
                return String(flex.institute_group_id) === String(groupId);
            })
            .map((item: any) => item.code);

        const campuses = MentorshipService.getAdminCampuses(domain, codes);
        if (campuses.some((campus) => campus.value === selectedId)) {
            return campuses;
        }

        const selectedCampus = MentorshipService.getAdminCampuses(domain, [selectedId]);
        if (selectedCampus.length > 0) {
            return [...selectedCampus, ...campuses.filter((campus) => campus.value !== selectedId)];
        }

        return [{ label: selected?.value || String(selectedId), value: selectedId }, ...campuses];
    }

    static programsForCampuses(domain: Map<string, any[]> | undefined, campusIds: number[]): MentorshipProgramOption[] {
        return MentorshipService.programsFromRows(domain?.get('ALL_PROGRAM_CODE') ?? [], campusIds);
    }

    /**
     * Maps ALL_PROGRAM_CODE-shaped rows to picker options. Rows come either from the domain-data
     * snapshot or, preferred, from the campus config API, which reads the same database the
     * eligible-student count filters on.
     */
    static programsFromRows(rows: any[], campusIds: number[]): MentorshipProgramOption[] {
        const programs = Array.isArray(rows) ? rows : [];
        const campusSet = new Set(campusIds.map((id) => String(id)));
        // ALL_PROGRAM_CODE can carry the same (campus, program) pair more than once, which rendered
        // the option twice with one shared checkbox state. De-duplicate on campus + program so no
        // distinct program is ever dropped — two programs sharing a name keep both rows.
        const seen = new Set<string>();
        return programs
            .filter((item: any) => campusSet.has(String(item.parentCode)))
            .map((item: any) => {
                const programId = MentorshipService.toNumberOrNull(item.code);
                const campusId = MentorshipService.toNumberOrNull(item.parentCode);
                if (programId === null || campusId === null) return null;
                const key = `${campusId}:${programId}`;
                if (seen.has(key)) return null;
                seen.add(key);
                return {
                    programId,
                    name: item.value,
                    campusId,
                    years: MentorshipService.yearsFromProgramDuration(item.flexCol),
                    programLevel: MentorshipService.programLevelFromFlex(item.flexCol),
                };
            })
            .filter((item): item is MentorshipProgramOption => item !== null);
    }

    static async listCohorts(instituteId?: number | null): Promise<MentorshipCohortListItem[]> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/cohorts`, MentorshipService.withInstituteQuery(instituteId));
        const data = MentorshipService.unwrap<any[]>(response);
        return Array.isArray(data) ? data.map((row) => MentorshipService.mapCohortIdentity(row)) : [];
    }

    static async getCohort(cohortId: number, instituteId?: number | null): Promise<MentorshipCohortDetail> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapCohortDetail(MentorshipService.unwrap(response));
    }

    static async createCohort(
        payload: MentorshipCreateCohortPayload,
        instituteId?: number | null
    ): Promise<MentorshipCohortDetail> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts`,
            payload,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapCohortDetail(MentorshipService.unwrap(response));
    }

    static async updateCohort(
        cohortId: number,
        payload: MentorshipCohortIdentityPayload,
        instituteId?: number | null
    ): Promise<MentorshipCohortDetail> {
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}`,
            payload,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapCohortDetail(MentorshipService.unwrap(response));
    }

    static async saveCampusConfig(
        cohortId: number,
        campusId: number,
        payload: MentorshipCampusConfigPayload,
        instituteId?: number | null
    ): Promise<MentorshipCampusConfig> {
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/campuses/${campusId}/config`,
            MentorshipService.payloadFromCampusConfig(payload),
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapCampusConfig(MentorshipService.unwrap(response));
    }

    /**
     * Live eligibility counts for an unsaved Campus Configuration draft. Read-only; the stored
     * config is untouched. Null counts mean the draft has no usable threshold / programs yet.
     */
    static async previewCampusEligibility(
        cohortId: number,
        campusId: number,
        threshold: string | null,
        programIds: number[],
        instituteId?: number | null
    ): Promise<{ eligibleAlumniCount: number | null; eligibleStudentCount: number | null }> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/campuses/${campusId}/config/eligibility-preview`,
            MentorshipService.withInstituteQuery(instituteId, {
                threshold: threshold && threshold.trim() !== '' ? threshold.trim() : undefined,
                programIds: programIds.length > 0 ? programIds.join(',') : undefined,
            })
        );
        const row: any = MentorshipService.unwrap(response);
        return {
            eligibleAlumniCount: MentorshipService.toNumberOrNull(
                row?.eligibleAlumniCount ?? row?.eligible_alumni_count
            ),
            eligibleStudentCount: MentorshipService.toNumberOrNull(
                row?.eligibleStudentCount ?? row?.eligible_student_count
            ),
        };
    }

    static async saveAllCampusesConfig(
        cohortId: number,
        payload: MentorshipCampusConfigPayload,
        instituteId?: number | null
    ): Promise<MentorshipCohortDetail> {
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/all-campuses/config`,
            MentorshipService.payloadFromCampusConfig(payload),
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapCohortDetail(MentorshipService.unwrap(response));
    }

    static mapTaxonomyChild(row: any): MentorshipTaxonomyChild {
        const id = MentorshipService.toNumberOrNull(row?.id);
        const child: MentorshipTaxonomyChild = {
            name: row?.name == null ? '' : String(row.name),
        };
        if (id !== null) {
            child.id = id;
        }
        return child;
    }

    static mapTaxonomyTrack(row: any): MentorshipTaxonomyTrack {
        const id = MentorshipService.toNumberOrNull(row?.id);
        const track: MentorshipTaxonomyTrack = {
            name: row?.name == null ? '' : String(row.name),
            children: Array.isArray(row?.children) ? row.children.map((item: any) => MentorshipService.mapTaxonomyChild(item)) : [],
            potentialMentorCount: MentorshipService.toNumberOrNull(row?.potentialMentorCount),
        };
        if (id !== null) {
            track.id = id;
        }
        return track;
    }

    static mapMappingState(row: any): MentorshipMappingState {
        const status = row?.status;
        const allowed: MentorshipMappingRunStatus[] = ['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED'];
        const lastCalculatedAt =
            row?.lastCalculatedAt == null || String(row.lastCalculatedAt).trim() === ''
                ? null
                : String(row.lastCalculatedAt);
        const availableAt = row?.recalculateAvailableAt;
        return {
            runId: MentorshipService.toNumberOrNull(row?.runId),
            status: allowed.includes(status) ? status : null,
            lastCalculatedAt,
            recalculateLocked: row?.recalculateLocked === true,
            recalculateAvailableAt:
                availableAt == null || String(availableAt).trim() === '' ? null : String(availableAt),
        };
    }

    static mapDomainTaxonomy(row: any): MentorshipDomainTaxonomy {
        return {
            tracks: Array.isArray(row?.tracks) ? row.tracks.map((item: any) => MentorshipService.mapTaxonomyTrack(item)) : [],
            mapping: MentorshipService.mapMappingState(row?.mapping),
        };
    }

    static payloadFromDomainTaxonomy(payload: MentorshipDomainTaxonomyPayload): MentorshipDomainTaxonomyPayload {
        const tracks = Array.isArray(payload?.tracks) ? payload.tracks : [];
        return {
            tracks: tracks.map((track) => {
                const row: MentorshipTaxonomyTrack = {
                    name: track?.name == null ? '' : String(track.name),
                    children: Array.isArray(track?.children)
                        ? track.children.map((child) => {
                              const item: MentorshipTaxonomyChild = {
                                  name: child?.name == null ? '' : String(child.name),
                              };
                              const childId = MentorshipService.toNumberOrNull(child?.id);
                              if (childId !== null) {
                                  item.id = childId;
                              }
                              return item;
                          })
                        : [],
                };
                const trackId = MentorshipService.toNumberOrNull(track?.id);
                if (trackId !== null) {
                    row.id = trackId;
                }
                return row;
            }),
        };
    }

    static formatLastCalculatedAt(value: string | null | undefined): string | null {
        if (value == null) return null;
        const text = String(value).trim();
        if (!text) return null;
        const matched = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
        if (matched) {
            return `${matched[1]} ${matched[2]}`;
        }
        if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
            return text.slice(0, 10);
        }
        return null;
    }

    static async getDomainTaxonomy(cohortId: number, instituteId?: number | null): Promise<MentorshipDomainTaxonomy> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/domain-taxonomy`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapDomainTaxonomy(MentorshipService.unwrap(response));
    }

    static async saveDomainTaxonomy(
        cohortId: number,
        payload: MentorshipDomainTaxonomyPayload,
        instituteId?: number | null
    ): Promise<MentorshipDomainTaxonomy> {
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/domain-taxonomy`,
            MentorshipService.payloadFromDomainTaxonomy(payload),
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapDomainTaxonomy(MentorshipService.unwrap(response));
    }

    static async recalculateDomainTaxonomy(
        cohortId: number,
        instituteId?: number | null
    ): Promise<MentorshipRecalculateResult> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/domain-taxonomy/recalculate`,
            {},
            MentorshipService.withInstituteQuery(instituteId)
        );
        const data = MentorshipService.unwrap<any>(response);
        const status = data?.status;
        return {
            runId: MentorshipService.toNumberOrNull(data?.runId),
            status:
                status === 'QUEUED' || status === 'RUNNING' || status === 'COMPLETED' || status === 'FAILED'
                    ? status
                    : 'QUEUED',
        };
    }

    static joinParam(values?: Array<string | number> | null): string | undefined {
        if (!values || values.length === 0) return undefined;
        return values.map((value) => String(value)).join(',');
    }

    static profilePictureSrc(path?: string | null): string | null {
        if (!path) return null;
        const value = String(path).trim();
        if (!value) return null;
        if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
        const base = process.env.REACT_APP_IMAGE_BASEPATH || '';
        if (!base) return value;
        if (value.startsWith('/')) return `${base}${value}`;
        return `${base}/${value}`;
    }

    static displayCurrentRole(company?: string | null, designation?: string | null): string {
        const parts = [company, designation]
            .map((value) => (value == null ? '' : String(value).trim()))
            .filter((value) => value !== '');
        return parts.join(' · ') || '—';
    }

    static displayConfirmationStatus(value?: string | null): string {
        if (value == null || String(value).trim() === '') return '—';
        return String(value);
    }

    static displayAssignedMentees(value?: number | null): string {
        if (value === null || value === undefined) return '0';
        return String(value);
    }

    static menteePoolConfiguredYearValues(eligiblePrograms: EligibleProgramSelection[] = []): number[] {
        const years = new Set<number>();
        for (const item of eligiblePrograms) {
            for (const year of item.years || []) {
                const n = MentorshipService.toNumberOrNull(year);
                if (n !== null) years.add(n);
            }
        }
        return Array.from(years).sort((a, b) => a - b);
    }

    static formatMenteePoolYearLabel(year: number): string {
        return `Year ${year}`;
    }

    /** Calendar pass_out_year options for Mentee Pool filter (current−10 … current+5). */
    static menteePoolPassOutYearOptions(now = new Date()): number[] {
        const current = now.getFullYear();
        const years: number[] = [];
        for (let year = current - 10; year <= current + 5; year += 1) {
            years.push(year);
        }
        return years;
    }

    static displayMenteePoolYear(batchYear?: number | string | null): string {
        if (batchYear == null) return '—';
        const text = String(batchYear).trim();
        return text === '' ? '—' : text;
    }

    static displayMenteePoolAssignedMentor(value?: string | null): string {
        const text = value == null ? '' : String(value).trim();
        return text === '' ? '—' : text;
    }

    static displayMenteePoolAssignmentStatus(
        confirmationStatus?: string | null,
        assignmentDisplayStatus?: string | null
    ): string {
        const derived = assignmentDisplayStatus == null ? '' : String(assignmentDisplayStatus).trim();
        if (derived !== '') return derived;
        return MentorshipService.displayMenteePoolConfirmationStatus(confirmationStatus);
    }

    static displayMenteePoolAreas(value?: string[] | string | null): string {
        if (value == null) return '—';
        if (Array.isArray(value)) {
            const labels = value.map((item) => String(item).trim()).filter((item) => item !== '');
            return labels.length > 0 ? labels.join(', ') : '—';
        }
        const text = String(value).trim();
        return text === '' ? '—' : text;
    }

    static displayMenteePoolConfirmationStatus(value?: string | null): string {
        const text = value == null ? '' : String(value).trim();
        if (text === '') return 'Yet to register';
        return text;
    }

    static displayMenteePoolGoals(value?: string | null): string {
        const text = value == null ? '' : String(value).trim();
        return text === '' ? '—' : text;
    }

    static uniqueMenteePoolMemberIds(ids: Array<number | null | undefined>): number[] {
        const seen = new Set<number>();
        const unique: number[] = [];
        for (const raw of ids) {
            const id = MentorshipService.toNumberOrNull(raw);
            if (id === null || seen.has(id)) continue;
            seen.add(id);
            unique.push(id);
        }
        return unique;
    }

    static menteePoolBulkCapError(action: MenteePoolBulkAction, ids: number[]): string | null {
        const unique = MentorshipService.uniqueMenteePoolMemberIds(ids);
        if (unique.length === 0) return 'Select at least one mentee.';
        if (unique.length > MENTEE_POOL_BULK_CAP) return MENTEE_POOL_BULK_OVER_LIMIT[action];
        return null;
    }

    static formatMenteePoolBulkResult(result: MentorshipMenteePoolBulkResult): string {
        const parts: string[] = [];
        if (result.action !== 'remove') {
            const reasons = (result.processed || []).map((item) =>
                String(item.registrationDelivery || '')
            );
            const sentCount = reasons.filter((value) => value === 'sent').length;
            const failedCount = reasons.filter((value) => value === 'send_failed').length;
            const notConfiguredCount = reasons.filter(
                (value) => value === MENTEE_POOL_REGISTRATION_DELIVERY
            ).length;
            const topDelivery = result.registrationDelivery || '';
            if (sentCount > 0) {
                parts.push(
                    sentCount === 1
                        ? MENTEE_POOL_DELIVERY_SENT_MESSAGE
                        : `Email sent to ${sentCount}.`
                );
            }
            if (failedCount > 0) {
                parts.push(
                    failedCount === 1
                        ? MENTEE_POOL_DELIVERY_FAILED_MESSAGE
                        : `Email delivery failed for ${failedCount}.`
                );
            }
            if (
                notConfiguredCount > 0 ||
                topDelivery === MENTEE_POOL_REGISTRATION_DELIVERY
            ) {
                parts.push(MENTEE_POOL_DELIVERY_NOT_CONFIGURED_MESSAGE);
            }
        }
        // Email actions already report sent/failed above; do not append "Processed N."
        if (result.action === 'remove' && (result.processed || []).length > 0) {
            parts.push(`Removed ${result.processed.length}.`);
        }
        // Group by reason and use readable wording. The raw "Failed: <memberId>: <reason_code>"
        // form leaked internal ids and snake_case codes into admin-facing text.
        const failedByReason = new Map<string, number>();
        for (const item of result.failed || []) {
            const reason = String(item.reason || '').trim() || 'failed';
            failedByReason.set(reason, (failedByReason.get(reason) ?? 0) + 1);
        }
        for (const [reason, count] of failedByReason) {
            parts.push(`${count} ${MentorshipService.describeBulkFailureReason(reason, count)}.`);
        }
        return parts.join(' ');
    }

    /**
     * Readable wording for a per-row import failure (File Upload / Watchlist / System Identified).
     * Phrased as a row-level explanation rather than a count. Unknown codes degrade to the code
     * with underscores removed so a new backend reason is still legible.
     */
    static describeUploadFailureReason(reason?: string | null): string {
        const code = String(reason || '').trim();
        if (!code) return '—';
        switch (code) {
            case 'insufficient_skills':
                return 'Needs at least 5 skills';
            case 'missing_identifier':
                return 'No uuid or email in this row';
            case 'invalid_file_row':
                return 'Malformed row — check the uuid format';
            case 'identifier_conflict':
                return 'uuid and email belong to different people';
            case 'user_not_found':
            case 'not_found':
                return 'No matching user found';
            case 'out_of_scope':
                return 'Alumni does not belong to this campus';
            case 'invalid_campus':
                return 'Missing or unknown institute_id';
            case 'campus_unresolved':
                return 'Campus could not be resolved — set institute_id';
            case 'no_tracks':
            case 'no_valid_tracks':
                return 'No career track given';
            case 'invalid_track':
                return 'Career track not in this cohort’s Domain Taxonomy';
            case 'already_in_pool':
                return 'Already in this pool';
            default:
                return code.replace(/_/g, ' ');
        }
    }

    /**
     * Readable wording for a bulk-action failure reason. Unknown codes degrade to the code with
     * underscores removed rather than being hidden, so a new backend reason is still legible.
     */
    static describeBulkFailureReason(reason: string, count: number): string {
        const plural = count === 1 ? 'was' : 'were';
        switch (reason) {
            case 'already_registered':
                return 'already registered';
            case 'already_in_pool':
                return 'already in the pool';
            case 'no_email':
                return `${plural} skipped — no email address`;
            case 'not_found':
            case 'user_not_found':
                return `${plural} not found`;
            case 'out_of_scope':
                return `${plural} outside your campus access`;
            case 'disabled_by_settings':
                return `${plural} skipped — notifications are turned off`;
            case 'disabled_by_mentor_preference':
                return `${plural} skipped — the mentor opted out`;
            case 'send_failed':
                return `${plural} not delivered`;
            case 'insufficient_skills':
                return `${plural} skipped — fewer than 5 skills`;
            case 'no_tracks':
            case 'no_valid_tracks':
                return `${plural} skipped — no career track selected`;
            case 'invalid_campus':
            case 'campus_unresolved':
                return `${plural} skipped — campus could not be resolved`;
            default:
                return `${plural} skipped — ${reason.replace(/_/g, ' ')}`;
        }
    }

    static menteePoolMessageRecipientUuid(member: MentorshipMenteePoolMember): string {
        return String(member?.student?.uuid || member?.studentId || '').trim();
    }

    static openEligibilitySettings(): void {
        window.open(MENTEE_POOL_MODULE_SETTINGS_PATH, '_blank', 'noopener,noreferrer');
    }

    static displayCapacity(value?: number | null): string {
        if (value === null || value === undefined) return '—';
        return String(value);
    }

    static messageRecipientUuid(member: MentorshipMentorPoolMember): string {
        const uuid = member?.alumni?.uuid || member?.alumniId || '';
        return String(uuid).trim();
    }

    /**
     * Opens the existing Messages app using the alumni-list / Phase 3 contract:
     * window.open(`/messages/${uuids}`, '_blank') with a hard cap of 20.
     * Does not write Mentorship pool data or call a Mentorship mailer.
     */
    static openExistingMessages(
        uuids: string[],
        copy?: { emptyMessage?: string; overLimitMessage?: string; limit?: number; pathPrefix?: string }
    ): { success: true } | { success: false; message: string } {
        const ids: string[] = [];
        const seen = new Set<string>();
        for (const raw of uuids) {
            const id = String(raw || '').trim();
            if (!id) continue;
            const key = id.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            ids.push(id);
        }
        if (ids.length === 0) {
            return { success: false, message: copy?.emptyMessage || 'Select at least one mentor to send a message.' };
        }
        if (ids.length > (copy?.limit ?? MENTOR_POOL_MESSAGE_RECIPIENT_LIMIT)) {
            return {
                success: false,
                message: copy?.overLimitMessage || MENTOR_POOL_MESSAGE_LIMIT_MESSAGE,
            };
        }
        const prefix = copy?.pathPrefix || '/messages';
        // window.open() returns null whenever `noopener` is set (and `noreferrer` implies it), so a
        // 'noopener,noreferrer' features string makes the blocked-popup guard below fire on every
        // click even when the tab opened fine. Target is a same-origin app route, so use the
        // Alumni List / Student List two-argument form and drop the opener by hand instead.
        const opened = window.open(`${prefix}/${ids.join(',')}`, '_blank');
        if (!opened) {
            return {
                success: false,
                message: 'Unable to open Messages. Allow pop-ups for this site, then try again.',
            };
        }
        opened.opener = null;
        return { success: true };
    }

    static openMenteePoolMessages(
        members: MentorshipMenteePoolMember[]
    ): { success: true } | { success: false; message: string } {
        return MentorshipService.openExistingMessages(
            members.map((member) => MentorshipService.menteePoolMessageRecipientUuid(member)),
            {
                emptyMessage: 'Select at least one mentee to send a message.',
                overLimitMessage: MENTEE_POOL_MESSAGE_LIMIT_MESSAGE,
                limit: MENTEE_POOL_MESSAGE_RECIPIENT_LIMIT,
                pathPrefix: '/messages/send-message',
            }
        );
    }

    static mapMentorPoolTrack(row: any): MentorshipMentorPoolTrack {
        return {
            id: MentorshipService.toNumberOrNull(row?.id) as number,
            name: row?.name ?? row?.trackName ?? null,
        };
    }

    static mapMentorPoolAlumni(row: any): MentorshipMentorPoolAlumni {
        return {
            name: row?.name ?? null,
            profilePicture: row?.profilePicture ?? null,
            uuid: row?.uuid ?? null,
            programName: row?.programName ?? null,
            batchYear: row?.batchYear ?? null,
            company: row?.company ?? null,
            designation: row?.designation ?? null,
            totalWorkex: MentorshipService.toNumberOrNull(row?.totalWorkex),
            expertise: row?.expertise ?? null,
            ap: row?.ap ?? null,
            mq: row?.mq ?? null,
            vl: row?.vl ?? null,
        };
    }

    static mapMentorPoolMember(row: any): MentorshipMentorPoolMember {
        return {
            id: MentorshipService.toNumberOrNull(row?.id) as number,
            alumniId: row?.alumniId == null ? '' : String(row.alumniId),
            campusId: MentorshipService.toNumberOrNull(row?.campusId),
            source: row?.source ?? null,
            confirmationStatus: row?.confirmationStatus ?? null,
            confirmationComment: row?.confirmationComment ?? null,
            invitedAt: row?.invitedAt ?? null,
            alumni: MentorshipService.mapMentorPoolAlumni(row?.alumni || {}),
            tracks: Array.isArray(row?.tracks) ? row.tracks.map((item: any) => MentorshipService.mapMentorPoolTrack(item)) : [],
            assignedMentees: MentorshipService.toNumberOrNull(row?.assignedMentees),
            maxMenteesPerMentor: MentorshipService.toNumberOrNull(row?.maxMenteesPerMentor),
        };
    }

    static mapPagination(row: any, fallbackPage = 1, fallbackPageSize = MENTOR_POOL_PAGE_SIZE_DEFAULT): MentorshipPagination {
        return {
            page: MentorshipService.toNumberOrNull(row?.page) ?? fallbackPage,
            pageSize: MentorshipService.toNumberOrNull(row?.pageSize) ?? fallbackPageSize,
            total: MentorshipService.toNumberOrNull(row?.total) ?? 0,
            totalPages: MentorshipService.toNumberOrNull(row?.totalPages) ?? 0,
        };
    }

    static mapMentorPoolCandidate(row: any): MentorshipMentorPoolCandidate {
        return {
            alumniId: row?.alumniId == null ? '' : String(row.alumniId),
            campusId: MentorshipService.toNumberOrNull(row?.campusId),
            campusAmbiguous: MentorshipService.asBoolean(row?.campusAmbiguous),
            campusName: row?.campusName ?? row?.campus_name ?? null,
            source: row?.source ?? null,
            watchlistCodes: Array.isArray(row?.watchlistCodes) ? row.watchlistCodes.map((item: any) => String(item)) : [],
            alumni: MentorshipService.mapMentorPoolAlumni(row?.alumni || {}),
            tracks: Array.isArray(row?.tracks) ? row.tracks.map((item: any) => MentorshipService.mapMentorPoolTrack(item)) : [],
        };
    }

    static mapImportResult(row: any): MentorshipImportResult {
        const imported = Array.isArray(row?.imported) ? row.imported : [];
        const failed = Array.isArray(row?.failed) ? row.failed : [];
        return {
            imported: imported.map((item: any) => ({
                alumniId: item?.alumniId == null ? undefined : String(item.alumniId),
                memberId: MentorshipService.toNumberOrNull(item?.memberId) ?? undefined,
                row: MentorshipService.toNumberOrNull(item?.row) ?? undefined,
                identifier: item?.identifier == null ? undefined : String(item.identifier),
                skippedTracks: Array.isArray(item?.skippedTracks)
                    ? item.skippedTracks.map((name: any) => String(name))
                    : undefined,
            })),
            failed: failed.map((item: any) => ({
                alumniId: item?.alumniId == null ? undefined : String(item.alumniId),
                row: MentorshipService.toNumberOrNull(item?.row) ?? undefined,
                identifier: item?.identifier == null ? undefined : String(item.identifier),
                reason: item?.reason == null ? undefined : String(item.reason),
            })),
            invitationDelivery: MentorshipService.mapInvitationDelivery(row?.invitationDelivery),
            skillsProfileWrite: row?.skillsProfileWrite == null ? null : String(row.skillsProfileWrite),
        };
    }

    static async listMentorPool(
        cohortId: number,
        query: MentorshipMentorPoolListQuery = {},
        instituteId?: number | null
    ): Promise<MentorshipMentorPoolListResult> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool`,
            MentorshipService.withInstituteQuery(instituteId, {
                search: query.search,
                company: query.company,
                campusId: query.campusId ?? undefined,
                confirmationStatus: MentorshipService.joinParam(query.confirmationStatus),
                includePreInvite: query.includePreInvite ? 1 : undefined,
                trackIds: MentorshipService.joinParam(query.trackIds),
                experience: MentorshipService.joinParam(query.experience),
                source: MentorshipService.joinParam(query.source),
                page: query.page,
                pageSize: query.pageSize,
            })
        );
        const data = MentorshipService.unwrap<any>(response);
        const members = Array.isArray(data?.members)
            ? data.members.map((item: any) => MentorshipService.mapMentorPoolMember(item))
            : Array.isArray(data)
              ? data.map((item: any) => MentorshipService.mapMentorPoolMember(item))
              : [];
        return {
            members,
            pagination: MentorshipService.mapPagination(
                data?.pagination,
                query.page ?? 1,
                query.pageSize ?? MENTOR_POOL_PAGE_SIZE_DEFAULT
            ),
        };
    }

    static async listSystemIdentifiedCandidates(
        cohortId: number,
        instituteId?: number | null
    ): Promise<{ mapping: MentorshipMappingState | null; candidates: MentorshipMentorPoolCandidate[] }> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool/system-identified`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        const data = MentorshipService.unwrap<any>(response);
        return {
            mapping: data?.mapping ? MentorshipService.mapMappingState(data.mapping) : null,
            candidates: Array.isArray(data?.candidates)
                ? data.candidates.map((item: any) => MentorshipService.mapMentorPoolCandidate(item))
                : [],
        };
    }

    static async importSystemIdentifiedCandidates(
        cohortId: number,
        alumniIds: string[],
        instituteId?: number | null
    ): Promise<MentorshipImportResult> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool/system-identified/import`,
            { alumniIds },
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapImportResult(MentorshipService.unwrap(response));
    }

    static async listWatchlistCandidates(
        cohortId: number,
        query: MentorshipWatchlistListQuery = {},
        instituteId?: number | null
    ): Promise<{ candidates: MentorshipMentorPoolCandidate[]; pagination: MentorshipPagination }> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool/watchlist`,
            MentorshipService.withInstituteQuery(instituteId, {
                search: query.search,
                expertise: query.expertise,
                company: query.company,
                designation: query.designation,
                quadrant: MentorshipService.joinParam(query.quadrant),
                experience: MentorshipService.joinParam(query.experience),
                ap: MentorshipService.joinParam(query.ap),
                mq: MentorshipService.joinParam(query.mq),
                vl: MentorshipService.joinParam(query.vl),
                campusId: query.campusId ?? undefined,
                page: query.page,
                pageSize: query.pageSize,
            })
        );
        const data = MentorshipService.unwrap<any>(response);
        return {
            candidates: Array.isArray(data?.candidates)
                ? data.candidates.map((item: any) => MentorshipService.mapMentorPoolCandidate(item))
                : [],
            pagination: MentorshipService.mapPagination(
                data?.pagination,
                query.page ?? 1,
                query.pageSize ?? MENTOR_POOL_PAGE_SIZE_DEFAULT
            ),
        };
    }

    static async importWatchlistCandidates(
        cohortId: number,
        body: {
            members?: Array<{ alumniId: string; campusId?: number | null; trackIds?: number[] }>;
            selectAll?: boolean;
            invite?: boolean;
        } & MentorshipWatchlistListQuery,
        instituteId?: number | null
    ): Promise<MentorshipImportResult> {
        const payload: Record<string, any> = {
            invite: Boolean(body.invite),
        };
        if (body.selectAll) {
            payload.selectAll = true;
            payload.search = body.search;
            payload.expertise = body.expertise;
            payload.company = body.company;
            payload.designation = body.designation;
            payload.quadrant = MentorshipService.joinParam(body.quadrant);
            payload.experience = MentorshipService.joinParam(body.experience);
            payload.ap = MentorshipService.joinParam(body.ap);
            payload.mq = MentorshipService.joinParam(body.mq);
            payload.vl = MentorshipService.joinParam(body.vl);
            payload.campusId = body.campusId ?? undefined;
        } else {
            payload.members = body.members || [];
        }
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool/watchlist/import`,
            payload,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapImportResult(MentorshipService.unwrap(response));
    }

    static async uploadMentorPoolFile(
        cohortId: number,
        file: File,
        invite?: boolean,
        instituteId?: number | null
    ): Promise<MentorshipImportResult> {
        const form = new FormData();
        form.append('file', file);
        if (invite) {
            form.append('invite', '1');
        }
        const query = MentorshipService.withInstituteQuery(instituteId);
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool/upload`,
            form,
            query
        );
        return MentorshipService.mapImportResult(MentorshipService.unwrap(response));
    }

    /** Mentee Pool bulk upload. Same CSV contract as the Mentor Pool upload. */
    static async uploadMenteePoolFile(
        cohortId: number,
        file: File,
        instituteId?: number | null
    ): Promise<MentorshipImportResult> {
        const form = new FormData();
        form.append('file', file);
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentee-pool/upload`,
            form,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapImportResult(MentorshipService.unwrap(response));
    }

    static async searchManualAddCandidates(
        cohortId: number,
        search: string,
        instituteId?: number | null
    ): Promise<MentorshipManualSearchCandidate[]> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool/manual-search`,
            MentorshipService.withInstituteQuery(instituteId, { search })
        );
        const data = MentorshipService.unwrap<any>(response);
        const rows = Array.isArray(data?.candidates) ? data.candidates : [];
        return rows.map((row: any) => ({
            alumniId: row?.alumniId == null ? '' : String(row.alumniId),
            uuid: row?.uuid ?? null,
            name: row?.name ?? null,
            company: row?.company ?? null,
            designation: row?.designation ?? null,
            skills: Array.isArray(row?.skills) ? row.skills.map((item: any) => String(item)) : [],
            alreadyInPool: MentorshipService.asBoolean(row?.alreadyInPool),
            campuses: Array.isArray(row?.campuses)
                ? row.campuses
                      .map((item: any) => ({
                          campusId: MentorshipService.toNumberOrNull(item?.campusId) as number,
                          programName: item?.programName ?? null,
                      }))
                      .filter((item: { campusId: number | null }) => item.campusId != null)
                : [],
        }));
    }

    static async addManualMentorPoolMember(
        cohortId: number,
        payload: MentorshipManualAddPayload,
        instituteId?: number | null
    ): Promise<MentorshipMentorPoolMember> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool/manual`,
            payload,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMentorPoolMember(MentorshipService.unwrap(response));
    }

    static async inviteMentorPoolMember(
        cohortId: number,
        memberId: number,
        instituteId?: number | null
    ): Promise<MentorshipMentorInviteResult> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool/${memberId}/invite`,
            {},
            MentorshipService.withInstituteQuery(instituteId)
        );
        const data = MentorshipService.unwrap<any>(response);
        return {
            sent: MentorshipService.asBoolean(data?.sent),
            reason: data?.reason == null ? null : String(data.reason),
            invitedAt: data?.invitedAt ?? null,
            confirmationStatus: data?.confirmationStatus ?? null,
        };
    }

    static async replaceMentorPoolMemberTracks(
        cohortId: number,
        memberId: number,
        trackIds: number[],
        instituteId?: number | null
    ): Promise<MentorshipMentorPoolMember> {
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool/${memberId}/tracks`,
            { trackIds },
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMentorPoolMember(MentorshipService.unwrap(response));
    }

    static async updateMentorPoolMemberStatus(
        cohortId: number,
        memberId: number,
        confirmationStatus: string,
        confirmationComment?: string | null,
        instituteId?: number | null
    ): Promise<MentorshipMentorPoolMember> {
        const payload: Record<string, any> = { confirmationStatus };
        if (confirmationComment !== undefined) {
            payload.confirmationComment = confirmationComment;
        }
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool/${memberId}/status`,
            payload,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMentorPoolMember(MentorshipService.unwrap(response));
    }

    static async removeMentorPoolMember(
        cohortId: number,
        memberId: number,
        instituteId?: number | null
    ): Promise<void> {
        await axiosClient().delete(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentor-pool/${memberId}`,
            MentorshipService.withInstituteQuery(instituteId)
        );
    }

    static mapMenteePoolStudent(row: any): MentorshipMenteePoolStudent {
        const batchYearRaw = row?.batchYear ?? row?.batch_year ?? null;
        const batchYearNum = MentorshipService.toNumberOrNull(batchYearRaw);
        return {
            name: row?.name ?? null,
            uuid: row?.uuid ?? null,
            email: row?.email ?? null,
            profilePicture: row?.profilePicture ?? row?.profile_picture ?? null,
            programName: row?.programName ?? row?.program_name ?? null,
            batchYear: batchYearNum ?? (batchYearRaw == null || String(batchYearRaw).trim() === '' ? null : batchYearRaw),
        };
    }

    static mapMenteePoolMember(row: any): MentorshipMenteePoolMember {
        const studentSource = {
            ...(row?.student || {}),
            batchYear:
                row?.student?.batchYear ??
                row?.student?.batch_year ??
                row?.batchYear ??
                row?.batch_year ??
                null,
            programName:
                row?.student?.programName ??
                row?.student?.program_name ??
                row?.programName ??
                row?.program_name ??
                null,
        };
        return {
            id: MentorshipService.toNumberOrNull(row?.id) as number,
            studentId: row?.studentId == null ? null : String(row.studentId),
            campusId: MentorshipService.toNumberOrNull(row?.campusId),
            confirmationStatus: row?.confirmationStatus ?? null,
            confirmationComment: row?.confirmationComment ?? null,
            student: MentorshipService.mapMenteePoolStudent(studentSource),
            tracks: Array.isArray(row?.tracks)
                ? row.tracks.map((item: any) => MentorshipService.mapMentorPoolTrack(item))
                : [],
            assignedMentor: row?.assignedMentor == null ? null : String(row.assignedMentor),
            assignmentDisplayStatus:
                row?.assignmentDisplayStatus == null ? null : String(row.assignmentDisplayStatus),
            preferredMentorshipAreas: row?.preferredMentorshipAreas ?? null,
            mentorshipGoals: row?.mentorshipGoals ?? null,
        };
    }

    static async listMenteePool(
        cohortId: number,
        query: MentorshipMenteePoolListQuery = {},
        instituteId?: number | null
    ): Promise<MentorshipMenteePoolListResult> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentee-pool`,
            MentorshipService.withInstituteQuery(instituteId, {
                search: query.search,
                campusId: query.campusId ?? undefined,
                confirmationStatus: MentorshipService.joinParam(query.confirmationStatus),
                trackIds: MentorshipService.joinParam(query.trackIds),
                programIds: MentorshipService.joinParam(query.programIds),
                years: MentorshipService.joinParam(query.years),
                page: query.page,
                pageSize: query.pageSize,
            })
        );
        const data = MentorshipService.unwrap<any>(response);
        const members = Array.isArray(data?.members)
            ? data.members.map((item: any) => MentorshipService.mapMenteePoolMember(item))
            : [];
        return {
            members,
            pagination: MentorshipService.mapPagination(
                data?.pagination,
                query.page ?? 1,
                query.pageSize ?? MENTEE_POOL_PAGE_SIZE_DEFAULT
            ),
        };
    }

    static async searchMenteePoolManualAddCandidates(
        cohortId: number,
        search: string,
        instituteId?: number | null
    ): Promise<MentorshipMenteePoolSearchCandidate[]> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentee-pool/manual-search`,
            MentorshipService.withInstituteQuery(instituteId, { search })
        );
        const data = MentorshipService.unwrap<any>(response);
        const rows = Array.isArray(data?.candidates) ? data.candidates : [];
        return rows.map((row: any) => ({
            studentId: row?.studentId == null ? '' : String(row.studentId),
            uuid: row?.uuid ?? null,
            name: row?.name ?? null,
            email: row?.email ?? null,
            alreadyInPool: MentorshipService.asBoolean(row?.alreadyInPool ?? row?.already_in_pool),
            campuses: Array.isArray(row?.campuses)
                ? row.campuses
                      .map((item: any) => ({
                          campusId: MentorshipService.toNumberOrNull(
                              item?.campusId ?? item?.campus_id
                          ) as number,
                          programName: item?.programName ?? item?.program_name ?? null,
                      }))
                      .filter((item: { campusId: number | null }) => item.campusId != null)
                : [],
        }));
    }

    static async addMenteePoolMember(
        cohortId: number,
        payload: MentorshipMenteePoolAddPayload,
        instituteId?: number | null
    ): Promise<MentorshipMenteePoolMember> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentee-pool`,
            payload,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMenteePoolMember(MentorshipService.unwrap(response));
    }

    static async replaceMenteePoolMemberTracks(
        cohortId: number,
        memberId: number,
        trackIds: number[],
        instituteId?: number | null
    ): Promise<MentorshipMenteePoolMember> {
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentee-pool/${memberId}/tracks`,
            { trackIds },
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMenteePoolMember(MentorshipService.unwrap(response));
    }

    static async updateMenteePoolMemberStatus(
        cohortId: number,
        memberId: number,
        confirmationStatus: string,
        confirmationComment?: string | null,
        instituteId?: number | null
    ): Promise<MentorshipMenteePoolMember> {
        const payload: Record<string, string | null> = { confirmationStatus };
        if (confirmationComment !== undefined) {
            payload.confirmationComment = confirmationComment;
        }
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentee-pool/${memberId}/status`,
            payload,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMenteePoolMember(MentorshipService.unwrap(response));
    }

    static async removeMenteePoolMember(
        cohortId: number,
        memberId: number,
        instituteId?: number | null
    ): Promise<void> {
        await axiosClient().delete(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentee-pool/${memberId}`,
            MentorshipService.withInstituteQuery(instituteId)
        );
    }

    static async requestMenteePoolRegistrationLink(
        cohortId: number,
        memberId: number,
        instituteId?: number | null
    ): Promise<{ id: number; confirmationStatus: string | null; registrationDelivery: string }> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentee-pool/${memberId}/registration-link`,
            {},
            MentorshipService.withInstituteQuery(instituteId)
        );
        const data = MentorshipService.unwrap<any>(response);
        return {
            id: MentorshipService.toNumberOrNull(data?.id) as number,
            confirmationStatus: data?.confirmationStatus ?? null,
            registrationDelivery: data?.registrationDelivery ?? MENTEE_POOL_REGISTRATION_DELIVERY,
        };
    }

    static async requestMenteePoolReminder(
        cohortId: number,
        memberId: number,
        instituteId?: number | null
    ): Promise<{ id: number; confirmationStatus: string | null; registrationDelivery: string }> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentee-pool/${memberId}/reminder`,
            {},
            MentorshipService.withInstituteQuery(instituteId)
        );
        const data = MentorshipService.unwrap<any>(response);
        return {
            id: MentorshipService.toNumberOrNull(data?.id) as number,
            confirmationStatus: data?.confirmationStatus ?? null,
            registrationDelivery: data?.registrationDelivery ?? MENTEE_POOL_REGISTRATION_DELIVERY,
        };
    }

    static async bulkMenteePoolActions(
        cohortId: number,
        action: MenteePoolBulkAction,
        memberIds: number[],
        instituteId?: number | null
    ): Promise<MentorshipMenteePoolBulkResult> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/mentee-pool/bulk`,
            { action, memberIds },
            MentorshipService.withInstituteQuery(instituteId)
        );
        const data = MentorshipService.unwrap<any>(response);
        return {
            action: data?.action,
            processed: Array.isArray(data?.processed)
                ? data.processed.map((item: any) => ({
                      memberId:
                          MentorshipService.toNumberOrNull(item?.memberId ?? item?.member_id) ??
                          undefined,
                      confirmationStatus: item?.confirmationStatus ?? item?.confirmation_status ?? null,
                      registrationDelivery:
                          item?.registrationDelivery ?? item?.registration_delivery ?? null,
                  }))
                : [],
            failed: Array.isArray(data?.failed)
                ? data.failed.map((item: any) => ({
                      memberId:
                          MentorshipService.toNumberOrNull(item?.memberId ?? item?.member_id) ??
                          undefined,
                      reason: item?.reason ?? undefined,
                  }))
                : [],
            registrationDelivery: data?.registrationDelivery ?? data?.registration_delivery ?? null,
        };
    }

    static matchingFilterParams(query: MentorshipMatchingPairListQuery = {}): Record<string, string | number | undefined> {
        return {
            search: query.search?.trim() || undefined,
            campusId: query.campusId ?? undefined,
            programIds: MentorshipService.joinParam(query.programIds),
            years: MentorshipService.joinParam(query.years),
            trackIds: MentorshipService.joinParam(query.trackIds),
            assignmentStatus: MentorshipService.joinParam(query.assignmentStatus),
        };
    }

    static matchingFilterBody(query: MentorshipMatchingPairListQuery = {}): Record<string, unknown> {
        const body: Record<string, unknown> = {};
        const search = query.search?.trim();
        if (search) body.search = search;
        if (query.campusId != null) body.campusId = query.campusId;
        if (query.programIds && query.programIds.length > 0) body.programIds = query.programIds;
        if (query.years && query.years.length > 0) body.years = query.years;
        if (query.trackIds && query.trackIds.length > 0) body.trackIds = query.trackIds;
        if (query.assignmentStatus && query.assignmentStatus.length > 0) {
            body.assignmentStatus = query.assignmentStatus;
        }
        return body;
    }

    static hasMatchingFilters(query: MentorshipMatchingPairListQuery = {}): boolean {
        return Object.keys(MentorshipService.matchingFilterBody(query)).length > 0;
    }

    static displayMatchingAssignmentStatus(value?: string | null): string {
        const text = value == null ? '' : String(value).trim();
        if (text === '' || text === 'Pending Decision') return 'Pending Decision';
        if (text === 'Confirmed') return 'Confirmed Pairing';
        if (text === 'Published') return 'Published Pairing';
        return text;
    }

    /**
     * Mentor Pool confirmation_status is one of {Invited | Confirmed | Onboarded | Declined},
     * NULL pre-invite. Only Declined is treated as a negative state here.
     */
    static isMentorPoolDeclined(value?: string | null): boolean {
        return value != null && String(value).trim().toLowerCase() === 'declined';
    }

    static mapMatchingTrack(row: any): MentorshipMatchingPairTrack {
        return {
            id: MentorshipService.toNumberOrNull(row?.id),
            name: row?.name == null ? null : String(row.name),
        };
    }

    static matchingInstituteAdminRemaining(iaRunCount?: number | null): number {
        const used = MentorshipService.toNumberOrNull(iaRunCount) ?? 0;
        return Math.max(0, MATCHING_IA_RUN_LIMIT - used);
    }

    static mapMatchingGate(row: any): MentorshipMatchingGate {
        return {
            requiredSum: MentorshipService.toNumberOrNull(row?.requiredSum),
            threshold: MentorshipService.toNumberOrNull(row?.threshold),
            confirmedOrOnboardedMentors: MentorshipService.toNumberOrNull(row?.confirmedOrOnboardedMentors) ?? 0,
            registeredMentees: MentorshipService.toNumberOrNull(row?.registeredMentees) ?? 0,
            unlocked: MentorshipService.asBoolean(row?.unlocked),
            iaRunCount: MentorshipService.toNumberOrNull(row?.iaRunCount) ?? 0,
        };
    }

    static mapMatchingStats(row: any): MentorshipMatchingStats {
        return {
            currentRunId: MentorshipService.toNumberOrNull(row?.currentRunId),
            confirmedMentors: MentorshipService.toNumberOrNull(row?.confirmedMentors) ?? 0,
            registeredMentees: MentorshipService.toNumberOrNull(row?.registeredMentees) ?? 0,
            totalPairings: MentorshipService.toNumberOrNull(row?.totalPairings) ?? 0,
            pendingDecision: MentorshipService.toNumberOrNull(row?.pendingDecision) ?? 0,
            confirmedPairings: MentorshipService.toNumberOrNull(row?.confirmedPairings) ?? 0,
            publishedPairings: MentorshipService.toNumberOrNull(row?.publishedPairings) ?? 0,
            distinctMenteesPaired: MentorshipService.toNumberOrNull(row?.distinctMenteesPaired) ?? 0,
        };
    }

    static mapCohortProgressPair(row: any): MentorshipCohortProgressPair {
        return {
            pairId: MentorshipService.toNumberOrNull(row?.pairId) ?? 0,
            menteeName: row?.menteeName == null || String(row.menteeName).trim() === '' ? null : String(row.menteeName),
            mentorName: row?.mentorName == null || String(row.mentorName).trim() === '' ? null : String(row.mentorName),
            interactionCount: MentorshipService.toNumberOrNull(row?.interactionCount) ?? 0,
            sessionCount: MentorshipService.toNumberOrNull(row?.sessionCount) ?? 0,
            isComplete: MentorshipService.asBoolean(row?.isComplete),
            isInactive: MentorshipService.asBoolean(row?.isInactive),
            lastActivityAt: row?.lastActivityAt == null ? null : String(row.lastActivityAt).slice(0, 10),
        };
    }

    static mapCohortProgress(row: any): MentorshipCohortProgress {
        const pairs = Array.isArray(row?.pairs) ? row.pairs : [];
        return {
            windowStart: row?.windowStart == null ? null : String(row.windowStart).slice(0, 10),
            windowEnd: row?.windowEnd == null ? null : String(row.windowEnd).slice(0, 10),
            hasDefinition: MentorshipService.asBoolean(row?.hasDefinition),
            interactionsEnabled: MentorshipService.asBoolean(row?.interactionsEnabled),
            interactionsMinimum: MentorshipService.toNumberOrNull(row?.interactionsMinimum) ?? 0,
            oneToOneSessionsEnabled: MentorshipService.asBoolean(row?.oneToOneSessionsEnabled),
            oneToOneSessionsMinimum: MentorshipService.toNumberOrNull(row?.oneToOneSessionsMinimum) ?? 0,
            currentRunId: MentorshipService.toNumberOrNull(row?.currentRunId),
            publishedPairCount: MentorshipService.toNumberOrNull(row?.publishedPairCount) ?? 0,
            completePairCount: MentorshipService.toNumberOrNull(row?.completePairCount) ?? 0,
            inWindowSessionTotal: MentorshipService.toNumberOrNull(row?.inWindowSessionTotal) ?? 0,
            inWindowInteractionTotal: MentorshipService.toNumberOrNull(row?.inWindowInteractionTotal) ?? 0,
            inactivePairCount: MentorshipService.toNumberOrNull(row?.inactivePairCount) ?? 0,
            sessionProgressPercent:
                row?.sessionProgressPercent == null
                    ? null
                    : MentorshipService.toNumberOrNull(row.sessionProgressPercent),
            interactionProgressPercent:
                row?.interactionProgressPercent == null
                    ? null
                    : MentorshipService.toNumberOrNull(row.interactionProgressPercent),
            pairs: pairs.map((pair: any) => MentorshipService.mapCohortProgressPair(pair)),
        };
    }

    static mapMatchingRun(row: any): MentorshipMatchingRun {
        const status = row?.status;
        const allowed: MentorshipMatchingRunStatus[] = ['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED'];
        return {
            id: MentorshipService.toNumberOrNull(row?.id) as number,
            cohortId: MentorshipService.toNumberOrNull(row?.cohortId),
            status: allowed.includes(status) ? status : null,
            initiatorRole: row?.initiatorRole == null ? null : String(row.initiatorRole),
            startedAt: row?.startedAt ?? null,
            finishedAt: row?.finishedAt ?? null,
            createdAt: row?.createdAt ?? null,
            updatedAt: row?.updatedAt ?? null,
        };
    }

    static mapMatchingOtherMenteeMatch(row: any): MentorshipMatchingOtherMenteeMatch {
        return {
            menteeId: MentorshipService.toNumberOrNull(
                row?.menteeId ?? row?.menteePoolMemberId ?? row?.mentee_pool_member_id
            ),
            name: row?.name == null ? null : String(row.name),
            email: row?.email == null ? null : String(row.email),
            programName: row?.programName ?? row?.program_name ?? null,
            batchYear: row?.batchYear ?? row?.batch_year ?? null,
            rank: MentorshipService.toNumberOrNull(row?.rank),
            isSelected: MentorshipService.toNumberOrNull(row?.isSelected ?? row?.is_selected) === 1,
            assignmentStatus: row?.assignmentStatus ?? row?.assignment_status ?? null,
        };
    }

    static mapMatchingPair(row: any): MentorshipMatchingPair {
        const mentee = row?.mentee || {};
        const mentor = row?.mentor || {};
        const otherDetailsRaw = row?.otherMenteeMatchDetails ?? row?.other_mentee_match_details;
        return {
            id: MentorshipService.toNumberOrNull(row?.id) as number,
            runId: MentorshipService.toNumberOrNull(row?.runId),
            rank: MentorshipService.toNumberOrNull(row?.rank),
            isSelected: MentorshipService.toNumberOrNull(row?.isSelected) === 1,
            assignmentStatus: row?.assignmentStatus ?? null,
            reasoning: row?.reasoning ?? null,
            otherMenteeMatches: MentorshipService.toNumberOrNull(row?.otherMenteeMatches) ?? 0,
            otherMenteeMatchDetails: Array.isArray(otherDetailsRaw)
                ? otherDetailsRaw.map((item: any) => MentorshipService.mapMatchingOtherMenteeMatch(item))
                : [],
            remainingCapacity: MentorshipService.toNumberOrNull(row?.remainingCapacity),
            mentee: {
                id: MentorshipService.toNumberOrNull(mentee.id),
                campusId: MentorshipService.toNumberOrNull(mentee.campusId),
                name: mentee.name ?? null,
                email: mentee.email ?? null,
                uuid: mentee.uuid ?? null,
                profilePicture: mentee.profilePicture ?? null,
                programId: MentorshipService.toNumberOrNull(mentee.programId),
                programName: mentee.programName ?? null,
                batchYear: mentee.batchYear ?? null,
                eligibleYears: Array.isArray(mentee.eligibleYears)
                    ? mentee.eligibleYears
                          .map((year: any) => MentorshipService.toNumberOrNull(year))
                          .filter((year: number | null): year is number => year !== null)
                    : [],
                tracks: Array.isArray(mentee.tracks) ? mentee.tracks.map((track: any) => MentorshipService.mapMatchingTrack(track)) : [],
            },
            mentor: {
                id: MentorshipService.toNumberOrNull(mentor.id),
                campusId: MentorshipService.toNumberOrNull(mentor.campusId),
                name: mentor.name ?? null,
                email: mentor.email ?? null,
                uuid: mentor.uuid ?? null,
                profilePicture: mentor.profilePicture ?? null,
                programName: mentor.programName ?? null,
                batchYear: mentor.batchYear ?? null,
                company: mentor.company ?? null,
                designation: mentor.designation ?? null,
                confirmationStatus: mentor.confirmationStatus ?? null,
                tracks: Array.isArray(mentor.tracks) ? mentor.tracks.map((track: any) => MentorshipService.mapMatchingTrack(track)) : [],
            },
        };
    }

    static mapMatchingSelectResult(row: any): MentorshipMatchingSelectResult {
        return {
            id: MentorshipService.toNumberOrNull(row?.id),
            runId: MentorshipService.toNumberOrNull(row?.runId),
            menteePoolMemberId: MentorshipService.toNumberOrNull(row?.menteePoolMemberId),
            mentorPoolMemberId: MentorshipService.toNumberOrNull(row?.mentorPoolMemberId),
            rank: MentorshipService.toNumberOrNull(row?.rank),
            isSelected: MentorshipService.toNumberOrNull(row?.isSelected) === 1,
            assignmentStatus: row?.assignmentStatus ?? null,
        };
    }

    static filenameFromContentDisposition(header?: string | null): string | null {
        if (!header) return null;
        const utf = String(header).match(/filename\*=UTF-8''([^;]+)/i);
        if (utf?.[1]) {
            try {
                return decodeURIComponent(utf[1].trim());
            } catch {
                return utf[1].trim();
            }
        }
        const simple = String(header).match(/filename="?([^";]+)"?/i);
        return simple?.[1] ? simple[1].trim() : null;
    }

    static triggerBlobDownload(blob: Blob, filename: string) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    static async messageFromRejectedBlob(error: any): Promise<string> {
        const data = error?.data;
        if (typeof Blob !== 'undefined' && data instanceof Blob) {
            try {
                const parsed = JSON.parse(await data.text());
                if (parsed?.message) return String(parsed.message);
            } catch {
                // fall through to the interceptor message
            }
        }
        return MentorshipService.getErrorMessage(error);
    }

    static async getMatchingGate(cohortId: number, instituteId?: number | null): Promise<MentorshipMatchingGate> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/matching/gate`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMatchingGate(MentorshipService.unwrap(response));
    }

    static async getMatchingStats(cohortId: number, instituteId?: number | null): Promise<MentorshipMatchingStats> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/matching/stats`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMatchingStats(MentorshipService.unwrap(response));
    }

    static async getCohortProgress(
        cohortId: number,
        instituteId?: number | null
    ): Promise<MentorshipCohortProgress> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/progress`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapCohortProgress(MentorshipService.unwrap(response));
    }

    static async exportCohortProgress(cohortId: number, instituteId?: number | null): Promise<void> {
        try {
            const response = await axiosClient().get(`${MENTORSHIP_BASE}/cohorts/${cohortId}/progress-export`, {
                ...MentorshipService.withInstituteQuery(instituteId),
                responseType: 'blob',
            });
            const contentType =
                response.headers?.['content-type'] ||
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const blob = new Blob([response.data], { type: contentType });
            const filename =
                MentorshipService.filenameFromContentDisposition(response.headers?.['content-disposition']) ||
                'mentorship-progress.xlsx';
            MentorshipService.triggerBlobDownload(blob, filename);
        } catch (error) {
            throw { ...(error as object), message: await MentorshipService.messageFromRejectedBlob(error) };
        }
    }

    static async enqueueMatchingRun(cohortId: number, instituteId?: number | null): Promise<MentorshipMatchingRun> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/matching/runs`,
            {},
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMatchingRun(MentorshipService.unwrap(response));
    }

    static async getMatchingRun(
        cohortId: number,
        runId: number,
        instituteId?: number | null
    ): Promise<MentorshipMatchingRun> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/matching/runs/${runId}`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMatchingRun(MentorshipService.unwrap(response));
    }

    static async listMatchingPairs(
        cohortId: number,
        query: MentorshipMatchingPairListQuery = {},
        instituteId?: number | null
    ): Promise<MentorshipMatchingPairListResult> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/matching/pairs`,
            MentorshipService.withInstituteQuery(instituteId, MentorshipService.matchingFilterParams(query))
        );
        const data = MentorshipService.unwrap<any>(response);
        return {
            runId: MentorshipService.toNumberOrNull(data?.runId),
            pairs: Array.isArray(data?.pairs) ? data.pairs.map((row: any) => MentorshipService.mapMatchingPair(row)) : [],
        };
    }

    static async selectMatchingPair(
        cohortId: number,
        pairId: number,
        instituteId?: number | null
    ): Promise<MentorshipMatchingSelectResult> {
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/matching/pairs/${pairId}/select`,
            {},
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMatchingSelectResult(MentorshipService.unwrap(response));
    }

    static async confirmMatchingPair(
        cohortId: number,
        pairId: number,
        instituteId?: number | null
    ): Promise<MentorshipMatchingSelectResult> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/matching/pairs/${pairId}/confirm`,
            {},
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapMatchingSelectResult(MentorshipService.unwrap(response));
    }

    static async confirmAllMatchingPairs(
        cohortId: number,
        query: MentorshipMatchingPairListQuery = {},
        instituteId?: number | null
    ): Promise<MentorshipMatchingConfirmAllResult> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/matching/confirm-all`,
            MentorshipService.matchingFilterBody(query),
            MentorshipService.withInstituteQuery(instituteId)
        );
        const data = MentorshipService.unwrap<any>(response);
        return {
            runId: MentorshipService.toNumberOrNull(data?.runId),
            confirmed: MentorshipService.toNumberOrNull(data?.confirmed) ?? 0,
            skipped: MentorshipService.toNumberOrNull(data?.skipped) ?? 0,
        };
    }

    /**
     * LLM match reasoning often embeds numeric campus_id (e.g. "Same campus (10010023846)").
     * Replace known parenthesized campus ids with institute labels for admin display.
     * Does not mutate stored DB values — display-only.
     */
    static formatMatchingReasoningWithCampusNames(
        reasoning: string | null | undefined,
        campuses: MentorshipCampusOption[] | null | undefined
    ): string {
        const raw = reasoning == null ? '' : String(reasoning);
        if (!raw.trim()) {
            return raw;
        }
        if (!Array.isArray(campuses) || campuses.length === 0) {
            return raw;
        }
        let out = raw;
        for (const campus of campuses) {
            const id = campus?.value;
            const name = String(campus?.label || '').trim();
            if (id == null || !name) {
                continue;
            }
            const escaped = String(id).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            out = out.replace(new RegExp(`\\(\\s*${escaped}\\s*\\)`, 'g'), `(${name})`);
        }
        return out;
    }

    /** Honest label for POST matching/publish `emails` (string or delivery counts object). */
    static formatMatchingPublishEmails(emails: unknown): string | null {
        if (emails == null) {
            return null;
        }
        if (typeof emails === 'string') {
            const text = emails.trim();
            return text || null;
        }
        if (typeof emails === 'object') {
            const row = emails as {
                reason?: string | null;
                sent?: number | null;
                send_failed?: number | null;
                sendFailed?: number | null;
            };
            const reason = row.reason == null ? '' : String(row.reason).trim();
            if (reason) {
                return reason;
            }
            const sent = MentorshipService.toNumberOrNull(row.sent) ?? 0;
            const failed =
                MentorshipService.toNumberOrNull(row.sendFailed) ??
                MentorshipService.toNumberOrNull(row.send_failed) ??
                0;
            return `${sent} sent, ${failed} failed`;
        }
        return String(emails);
    }

    static async publishMatchingPairs(
        cohortId: number,
        instituteId?: number | null
    ): Promise<MentorshipMatchingPublishResult> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/matching/publish`,
            { confirm: true },
            MentorshipService.withInstituteQuery(instituteId)
        );
        const data = MentorshipService.unwrap<any>(response);
        return {
            runId: MentorshipService.toNumberOrNull(data?.runId),
            published: MentorshipService.toNumberOrNull(data?.published) ?? 0,
            emails: MentorshipService.formatMatchingPublishEmails(data?.emails),
        };
    }

    static async exportMatchingPairs(
        cohortId: number,
        query: MentorshipMatchingPairListQuery = {},
        instituteId?: number | null
    ): Promise<void> {
        try {
            const response = await axiosClient().get(`${MENTORSHIP_BASE}/cohorts/${cohortId}/matching/export`, {
                ...MentorshipService.withInstituteQuery(instituteId, MentorshipService.matchingFilterParams(query)),
                responseType: 'blob',
            });
            const contentType =
                response.headers?.['content-type'] ||
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const blob = new Blob([response.data], { type: contentType });
            const filename =
                MentorshipService.filenameFromContentDisposition(response.headers?.['content-disposition']) ||
                'matching-export.xlsx';
            MentorshipService.triggerBlobDownload(blob, filename);
        } catch (error) {
            throw { ...(error as object), message: await MentorshipService.messageFromRejectedBlob(error) };
        }
    }

    static sessionLogFilterParams(query: MentorshipCohortSessionLogQuery = {}): Record<string, string | number | undefined> {
        return {
            search: query.search?.trim() || undefined,
            campusId: query.campusId ?? undefined,
            loggedByRole: query.loggedByRole || undefined,
            rating: query.rating || undefined,
            page: query.page,
            pageSize: query.pageSize,
        };
    }

    static mapCohortSessionLogRow(row: any): MentorshipCohortSessionLogRow {
        const rating = row?.rating;
        const starsFromNested =
            rating && typeof rating === 'object'
                ? MentorshipService.toNumberOrNull(rating.stars ?? rating.rating_stars)
                : null;
        return {
            id: MentorshipService.toNumberOrNull(row?.id) as number,
            occurredAt: row?.occurredAt ?? row?.occurred_at ?? null,
            pairId: MentorshipService.toNumberOrNull(row?.pairId ?? row?.pair_id),
            menteeName: row?.menteeName ?? row?.mentee_name ?? null,
            mentorName: row?.mentorName ?? row?.mentor_name ?? null,
            campusName: row?.campusName ?? row?.campus_name ?? null,
            type: row?.type == null || String(row.type).trim() === '' ? '1:1' : String(row.type),
            durationMinutes: MentorshipService.toNumberOrNull(row?.durationMinutes ?? row?.duration_minutes),
            title: row?.title == null || String(row.title).trim() === '' ? null : String(row.title),
            sessionLink: row?.sessionLink ?? row?.session_link ?? null,
            loggedByRole: row?.loggedByRole ?? row?.logged_by_role ?? null,
            source: row?.source == null ? null : String(row.source),
            ratingStars:
                starsFromNested ??
                MentorshipService.toNumberOrNull(row?.ratingStars ?? row?.rating_stars),
        };
    }

    static mapCohortSessionLogResult(data: any): MentorshipCohortSessionLogResult {
        return {
            sessions: Array.isArray(data?.sessions)
                ? data.sessions.map((row: any) => MentorshipService.mapCohortSessionLogRow(row))
                : [],
            pagination: MentorshipService.mapPagination(
                data?.pagination,
                1,
                SESSION_LOG_PAGE_SIZE_DEFAULT
            ),
            sessionCount: MentorshipService.toNumberOrNull(data?.sessionCount ?? data?.session_count) ?? 0,
            pairCount: MentorshipService.toNumberOrNull(data?.pairCount ?? data?.pair_count) ?? 0,
        };
    }

    static async listCohortSessions(
        cohortId: number,
        query: MentorshipCohortSessionLogQuery = {},
        instituteId?: number | null
    ): Promise<MentorshipCohortSessionLogResult> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/sessions`,
            MentorshipService.withInstituteQuery(instituteId, MentorshipService.sessionLogFilterParams(query))
        );
        return MentorshipService.mapCohortSessionLogResult(MentorshipService.unwrap(response));
    }

    static async exportCohortSessions(
        cohortId: number,
        query: MentorshipCohortSessionLogQuery = {},
        instituteId?: number | null
    ): Promise<void> {
        try {
            const params = MentorshipService.sessionLogFilterParams({
                search: query.search,
                campusId: query.campusId,
                loggedByRole: query.loggedByRole,
                rating: query.rating,
            });
            const response = await axiosClient().get(`${MENTORSHIP_BASE}/cohorts/${cohortId}/sessions-export`, {
                ...MentorshipService.withInstituteQuery(instituteId, params),
                responseType: 'blob',
            });
            const contentType =
                response.headers?.['content-type'] ||
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const blob = new Blob([response.data], { type: contentType });
            const filename =
                MentorshipService.filenameFromContentDisposition(response.headers?.['content-disposition']) ||
                'mentorship-session-log.xlsx';
            MentorshipService.triggerBlobDownload(blob, filename);
        } catch (error) {
            throw { ...(error as object), message: await MentorshipService.messageFromRejectedBlob(error) };
        }
    }

    static mapNotificationPreferences(row: any): MentorshipNotificationPreferences {
        if (!row) {
            return {
                mentorInvitationEmail: true,
                menteeOptInConfirmation: true,
                matchAnnouncement: true,
            };
        }
        return {
            mentorInvitationEmail: MentorshipService.asBoolean(row.mentorInvitationEmail ?? row.mentor_invitation_email),
            menteeOptInConfirmation: MentorshipService.asBoolean(
                row.menteeOptInConfirmation ?? row.mentee_opt_in_confirmation
            ),
            matchAnnouncement: MentorshipService.asBoolean(row.matchAnnouncement ?? row.match_announcement),
        };
    }

    static async getNotificationPreferences(
        cohortId: number,
        instituteId?: number | null
    ): Promise<MentorshipNotificationPreferences> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/notification-preferences`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapNotificationPreferences(MentorshipService.unwrap(response));
    }

    static async saveNotificationPreferences(
        cohortId: number,
        payload: MentorshipNotificationPreferences,
        instituteId?: number | null
    ): Promise<MentorshipNotificationPreferences> {
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/notification-preferences`,
            {
                mentorInvitationEmail: payload.mentorInvitationEmail === true,
                menteeOptInConfirmation: payload.menteeOptInConfirmation === true,
                matchAnnouncement: payload.matchAnnouncement === true,
            },
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapNotificationPreferences(MentorshipService.unwrap(response));
    }

    static mapDodPreferences(row: any): MentorshipDodPreferences {
        if (!row) {
            return {
                interactionsEnabled: true,
                interactionsMinimum: 1,
                oneToOneSessionsEnabled: true,
                oneToOneSessionsMinimum: 1,
                showDodProgressToMentors: true,
                showDodProgressToMentees: false,
                alertAdminWhenPairFallingBehind: true,
            };
        }
        return {
            interactionsEnabled: MentorshipService.asBoolean(
                row.interactionsEnabled ?? row.interactions_enabled
            ),
            interactionsMinimum:
                MentorshipService.toNumberOrNull(row.interactionsMinimum ?? row.interactions_minimum) ?? 1,
            oneToOneSessionsEnabled: MentorshipService.asBoolean(
                row.oneToOneSessionsEnabled ?? row.one_to_one_sessions_enabled
            ),
            oneToOneSessionsMinimum:
                MentorshipService.toNumberOrNull(
                    row.oneToOneSessionsMinimum ?? row.one_to_one_sessions_minimum
                ) ?? 1,
            showDodProgressToMentors: MentorshipService.asBoolean(
                row.showDodProgressToMentors ?? row.show_dod_progress_to_mentors
            ),
            showDodProgressToMentees: MentorshipService.asBoolean(
                row.showDodProgressToMentees ?? row.show_dod_progress_to_mentees
            ),
            alertAdminWhenPairFallingBehind: MentorshipService.asBoolean(
                row.alertAdminWhenPairFallingBehind ?? row.alert_admin_when_pair_falling_behind
            ),
        };
    }

    static async getDodPreferences(
        cohortId: number,
        instituteId?: number | null
    ): Promise<MentorshipDodPreferences> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/definition-of-done-preferences`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapDodPreferences(MentorshipService.unwrap(response));
    }

    static async saveDodPreferences(
        cohortId: number,
        payload: MentorshipDodPreferences,
        instituteId?: number | null
    ): Promise<MentorshipDodPreferences> {
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/definition-of-done-preferences`,
            {
                interactionsEnabled: payload.interactionsEnabled === true,
                interactionsMinimum: payload.interactionsMinimum,
                oneToOneSessionsEnabled: payload.oneToOneSessionsEnabled === true,
                oneToOneSessionsMinimum: payload.oneToOneSessionsMinimum,
                showDodProgressToMentors: payload.showDodProgressToMentors === true,
                showDodProgressToMentees: payload.showDodProgressToMentees === true,
                alertAdminWhenPairFallingBehind: payload.alertAdminWhenPairFallingBehind === true,
            },
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapDodPreferences(MentorshipService.unwrap(response));
    }

    static mapOfflineLoggingPreferences(row: any): MentorshipOfflineLoggingPreferences {
        const whoRaw = row?.whoCanLogOffline ?? row?.who_can_log_offline;
        const whoCanLogOffline: MentorshipWhoCanLogOffline =
            whoRaw === 'ANY_PARTICIPANT' || whoRaw === 'MENTOR_AND_ADMIN' || whoRaw === 'ADMIN_ONLY'
                ? whoRaw
                : 'ADMIN_ONLY';
        return {
            whoCanLogOffline,
            modeZoomTeamsMeet: MentorshipService.asBoolean(
                row?.modeZoomTeamsMeet ?? row?.mode_zoom_teams_meet
            ),
            modeTelephonic: MentorshipService.asBoolean(row?.modeTelephonic ?? row?.mode_telephonic),
            modeFacetimeWhatsapp: MentorshipService.asBoolean(
                row?.modeFacetimeWhatsapp ?? row?.mode_facetime_whatsapp
            ),
            modeLinkedin: MentorshipService.asBoolean(row?.modeLinkedin ?? row?.mode_linkedin),
            modeInPerson: MentorshipService.asBoolean(row?.modeInPerson ?? row?.mode_in_person),
            modeOthers: MentorshipService.asBoolean(row?.modeOthers ?? row?.mode_others),
        };
    }

    static async getOfflineLoggingPreferences(
        cohortId: number,
        instituteId?: number | null
    ): Promise<MentorshipOfflineLoggingPreferences> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/offline-logging-preferences`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapOfflineLoggingPreferences(MentorshipService.unwrap(response));
    }

    static async saveOfflineLoggingPreferences(
        cohortId: number,
        payload: MentorshipOfflineLoggingPreferences,
        instituteId?: number | null
    ): Promise<MentorshipOfflineLoggingPreferences> {
        const response = await axiosClient().put(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/offline-logging-preferences`,
            {
                whoCanLogOffline: payload.whoCanLogOffline,
                modeZoomTeamsMeet: payload.modeZoomTeamsMeet === true,
                modeTelephonic: payload.modeTelephonic === true,
                modeFacetimeWhatsapp: payload.modeFacetimeWhatsapp === true,
                modeLinkedin: payload.modeLinkedin === true,
                modeInPerson: payload.modeInPerson === true,
                modeOthers: payload.modeOthers === true,
            },
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapOfflineLoggingPreferences(MentorshipService.unwrap(response));
    }

    static mapPairSession(row: any): MentorshipPairSession {
        return {
            id: MentorshipService.toNumberOrNull(row?.id) as number,
            cohortId: MentorshipService.toNumberOrNull(row?.cohortId ?? row?.cohort_id),
            pairId: MentorshipService.toNumberOrNull(row?.pairId ?? row?.pair_id),
            source: row?.source == null ? null : String(row.source),
            occurredAt: row?.occurredAt ?? row?.occurred_at ?? null,
            durationMinutes: MentorshipService.toNumberOrNull(
                row?.durationMinutes ?? row?.duration_minutes
            ),
            sessionLink: row?.sessionLink ?? row?.session_link ?? null,
            title: row?.title == null ? null : String(row.title),
            loggedByRole: row?.loggedByRole ?? row?.logged_by_role ?? null,
            createdBy: row?.createdBy ?? row?.created_by ?? null,
            createdAt: row?.createdAt ?? row?.created_at ?? null,
            updatedAt: row?.updatedAt ?? row?.updated_at ?? null,
        };
    }

    static mapPairInteraction(row: any): MentorshipPairInteraction {
        return {
            id: MentorshipService.toNumberOrNull(row?.id) as number,
            cohortId: MentorshipService.toNumberOrNull(row?.cohortId ?? row?.cohort_id),
            pairId: MentorshipService.toNumberOrNull(row?.pairId ?? row?.pair_id),
            mode: row?.mode == null ? null : String(row.mode),
            occurredAt: row?.occurredAt ?? row?.occurred_at ?? null,
            notes: row?.notes == null ? null : String(row.notes),
            loggedByRole: row?.loggedByRole ?? row?.logged_by_role ?? null,
            createdBy: row?.createdBy ?? row?.created_by ?? null,
            createdAt: row?.createdAt ?? row?.created_at ?? null,
            updatedAt: row?.updatedAt ?? row?.updated_at ?? null,
        };
    }

    static mapPairSessionList(payload: any): MentorshipPairSession[] {
        const rows = Array.isArray(payload) ? payload : [];
        return rows.map((row) => MentorshipService.mapPairSession(row));
    }

    static mapPairInteractionList(payload: any): MentorshipPairInteraction[] {
        const rows = Array.isArray(payload) ? payload : [];
        return rows.map((row) => MentorshipService.mapPairInteraction(row));
    }

    static async listPairSessions(
        cohortId: number,
        pairId: number,
        instituteId?: number | null
    ): Promise<MentorshipPairSession[]> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/pairs/${pairId}/sessions`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapPairSessionList(MentorshipService.unwrap(response));
    }

    static async createPairSession(
        cohortId: number,
        pairId: number,
        payload: MentorshipCreatePairSessionPayload,
        instituteId?: number | null
    ): Promise<MentorshipPairSession> {
        const body: Record<string, unknown> = { occurredAt: payload.occurredAt };
        if (payload.sessionLink != null && payload.sessionLink !== '') {
            body['sessionLink'] = payload.sessionLink;
        }
        if (payload.title != null && payload.title !== '') {
            body.title = payload.title;
        }
        if (payload.durationMinutes != null) {
            body['durationMinutes'] = payload.durationMinutes;
        }
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/pairs/${pairId}/sessions`,
            body,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapPairSession(MentorshipService.unwrap(response));
    }

    static async listPairInteractions(
        cohortId: number,
        pairId: number,
        instituteId?: number | null
    ): Promise<MentorshipPairInteraction[]> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/pairs/${pairId}/interactions`,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapPairInteractionList(MentorshipService.unwrap(response));
    }

    static async createPairInteraction(
        cohortId: number,
        pairId: number,
        payload: MentorshipCreatePairInteractionPayload,
        instituteId?: number | null
    ): Promise<MentorshipPairInteraction> {
        const body: Record<string, unknown> = {
            occurredAt: payload.occurredAt,
            mode: payload.mode,
        };
        if (payload.notes != null && payload.notes !== '') {
            body['notes'] = payload.notes;
        }
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/pairs/${pairId}/interactions`,
            body,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapPairInteraction(MentorshipService.unwrap(response));
    }

    static async listAdminBookingSlots(
        cohortId: number,
        pairId: number,
        month: string,
        instituteId?: number | null
    ): Promise<MentorshipAdminBookingSlotsPayload> {
        const response = await axiosClient().get(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/pairs/${pairId}/booking/slots`,
            MentorshipService.withInstituteQuery(instituteId, { month })
        );
        return MentorshipService.mapAdminBookingSlots(MentorshipService.unwrap<any>(response));
    }

    static async createAdminPairBooking(
        cohortId: number,
        pairId: number,
        payload: MentorshipAdminCreateBookingPayload,
        instituteId?: number | null
    ): Promise<MentorshipAdminBookingRow> {
        const body: Record<string, unknown> = {
            date: payload.date,
            startMinute: payload.startMinute,
            topic: payload.topic,
            mode: payload.mode,
        };
        if (payload.agenda != null && payload.agenda !== '') {
            body.agenda = payload.agenda;
        }
        if (payload.sessionLink != null && payload.sessionLink !== '') {
            body.sessionLink = payload.sessionLink;
        }
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/cohorts/${cohortId}/pairs/${pairId}/bookings`,
            body,
            MentorshipService.withInstituteQuery(instituteId)
        );
        return MentorshipService.mapAdminBookingRow(MentorshipService.unwrap<any>(response));
    }

    static mapAdminBookingSlots(data: any): MentorshipAdminBookingSlotsPayload {
        const slots = Array.isArray(data?.slots) ? data.slots : [];
        return {
            month: data?.month == null ? '' : String(data.month),
            pairId: MentorshipService.toNumberOrNull(data?.pairId ?? data?.pair_id),
            mentorAlumniId: data?.mentorAlumniId ?? data?.mentor_alumni_id ?? null,
            mentorName: data?.mentorName ?? data?.mentor_name ?? null,
            menteeName: data?.menteeName ?? data?.mentee_name ?? null,
            slotDurationMinutes: MentorshipService.toNumberOrNull(
                data?.slotDurationMinutes ?? data?.slot_duration_minutes
            ),
            timezoneLabel: data?.timezoneLabel ?? data?.timezone_label ?? null,
            slots: slots.map((row: any) => ({
                date: row?.date == null ? '' : String(row.date),
                startMinute: MentorshipService.toNumberOrNull(row?.startMinute ?? row?.start_minute) ?? 0,
                durationMinutes:
                    MentorshipService.toNumberOrNull(row?.durationMinutes ?? row?.duration_minutes) ?? 0,
                state: String(row?.state || 'available') as MentorshipAdminBookingSlot['state'],
            })),
        };
    }

    static mapAdminBookingRow(row: any): MentorshipAdminBookingRow {
        return {
            id: MentorshipService.toNumberOrNull(row?.id) ?? 0,
            pairId: MentorshipService.toNumberOrNull(row?.pairId ?? row?.pair_id),
            bookedDate: row?.bookedDate ?? row?.booked_date ?? null,
            startMinute: MentorshipService.toNumberOrNull(row?.startMinute ?? row?.start_minute),
            durationMinutes: MentorshipService.toNumberOrNull(row?.durationMinutes ?? row?.duration_minutes),
            topic: row?.topic == null ? '' : String(row.topic),
            mode: row?.mode == null ? null : String(row.mode),
            sessionLink: row?.sessionLink ?? row?.session_link ?? null,
            bookedByRole: row?.bookedByRole ?? row?.booked_by_role ?? null,
            emailResult: row?.emailResult ?? row?.email_result ?? null,
        };
    }
}

export default MentorshipService;
